import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import StorageService from '../../../services/StorageService';
import { API_URLS } from '../../../constants/api-urls';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import { useLocationMaster } from '../../../hooks/useLocationMaster';
import usePlatformLogo from '../../../hooks/usePlatformLogo';
import '../../../assets/styles/generate_invoice.scss';

export default function GenerateInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const platformLogoUrl = usePlatformLogo();
  const [loading, setLoading] = useState(true);
  const [cmRfqIdFromPo, setCmRfqIdFromPo] = useState(null);
  const [existingInvoiceId, setExistingInvoiceId] = useState(null);
  
  // Master Data Hooks
  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { shippingTermsOptions, paymentScheduleOptions, modeOfTransportOptions } = useFactoryDetailsMaster();
  const { countries, states, cities, updateListsForCountry, updateListsForState } = useLocationMaster();
  
  const [tradeType, setTradeType] = useState('');
  const [tradeScenario, setTradeScenario] = useState('');
  
  // Determine trade type flags based on tradeType
  const tradeTypeLower = (tradeType || '').toLowerCase();
  const isIntraState = tradeTypeLower.includes('intra-state') || 
                      (tradeTypeLower.includes('cgst') && tradeTypeLower.includes('sgst'));
  const isImport = tradeTypeLower.includes('import') || 
                  tradeTypeLower.includes('cif') || 
                  tradeTypeLower.includes('bcd');
  const isThirdCountry = tradeTypeLower.includes('third country');
  const isExport = tradeTypeLower.includes('export') || 
                  tradeTypeLower.includes('lut');
  const isInterState = tradeTypeLower.includes('inter-state') || 
                      (tradeTypeLower.includes('igst') && !isExport && !isImport && !isIntraState);
  
  const [formData, setFormData] = useState({
    // Invoice Details
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    poReference: '',
    poDate: '',
    currency: 'INR',
    
    // Export Type
    exportType: 'With LUT (0% IGST)',
    lutNumber: '',
    lutDate: '',
    arNumber: '',
    arDate: '',
    placeOfSupply: '',
    
    // Payment Terms
    paymentTerms: '',
    paymentDueWithin: '',
    advancePayment: '',
    balancePayment: '',
    
    // Shipping
    shippingMethod: '',
    portOfLoading: '',
    portOfDischarge: '',
    shippingAddress: '',
    shippingCountry: '',
    shippingState: '',
    shippingCity: '',
    shippingPincode: '',
    
    // Delivery Terms
    tradeTerms: '',
    deliveryTime: '',
    deliveryTimeUnit: 'WEEKS',
    
    // Packing
    noOfPackages: '',
    packagingType: '',
    grossWeight: '',
    netWeight: '',
    dimensions: '',
    
    // Additional Notes
    additionalNotes: ''
  });

  const [sellerDetails, setSellerDetails] = useState({
    companyName: '',
    address: [],
    gstin: '',
    iec: '',
    email: '',
    phone: ''
  });

  const [buyerDetails, setBuyerDetails] = useState({
    companyName: '',
    address: [],
    taxId: '',
    placeOfSupply: '',
    email: '',
    phone: ''
  });

  const [lineItems, setLineItems] = useState([]);

  const [bankDetails, setBankDetails] = useState({
    bankName1: '',
    accountName1: '',
    accountNumber1: '',
    swiftIfsc1: '',
    iban1: '',
    branch1: '',
    bankName2: '',
    accountName2: '',
    accountNumber2: '',
    swiftIfsc2: '',
    iban2: '',
    branch2: ''
  });

  // Fetch purchase order data
  useEffect(() => {
    const fetchPurchaseOrderData = async () => {
      try {
        setLoading(true);
        const poId = location.state?.poId;
        const invoiceId = location.state?.invoiceId;
        
        if (!poId) {
          toast.error('Purchase Order ID is missing');
          navigate('/invoice-list');
          return;
        }

        // If invoiceId is provided, fetch invoice data first
        let invoiceData = null;
        const invoiceIdToLoad = invoiceId || existingInvoiceId;
        if (invoiceIdToLoad) {
          try {
            const invoiceResponse = await CommonService.get(`/seller-invoice/${invoiceIdToLoad}`);
            if (invoiceResponse && invoiceResponse.success && invoiceResponse.data) {
              invoiceData = invoiceResponse.data;
              console.log('Invoice data fetched:', invoiceData);
            } else {
              toast.warning('Invoice not found, loading from purchase order');
            }
          } catch (invoiceError) {
            console.error('Error fetching invoice data:', invoiceError);
            toast.warning('Failed to fetch invoice data, loading from purchase order');
          }
        }

        // Fetch purchase order details
        const poResponse = await CommonService.get(`/purchase-order/${poId}`);
        
        if (!poResponse || !poResponse.success || !poResponse.data) {
          toast.error('Failed to fetch purchase order details');
          navigate('/invoice-list');
          return;
        }

        const poData = poResponse.data;
        // CM production: PO rows created for CM have cm_rfq_id
        if (poData?.cm_rfq_id != null && String(poData.cm_rfq_id).trim() !== "") {
          const n = Number(poData.cm_rfq_id);
          setCmRfqIdFromPo(Number.isFinite(n) && n > 0 ? n : poData.cm_rfq_id);
        } else {
          setCmRfqIdFromPo(null);
        }
        
        console.log('Purchase Order Data:', {
          seller_company_id: poData.seller_company_id,
          buyer_company_id: poData.buyer_company_id,
          seller_company_name: poData.seller_company_name,
          buyer_company_name: poData.buyer_company_name
        });

        // If no invoiceId was passed, try to load existing invoice for this PO
        if (!invoiceId) {
          try {
            const invByPoRes = await CommonService.get(`/seller-invoice/purchase-order/${poId}`);
            if (invByPoRes && invByPoRes.success && invByPoRes.data?.id) {
              setExistingInvoiceId(invByPoRes.data.id);
              invoiceData = invoiceData || invByPoRes.data;
            }
          } catch {
            // ignore - means no invoice exists yet for this PO
          }
        }

        // Fetch RFQ data if available
        let rfqData = null;
        if (poData.rfq_id) {
          try {
            const rfqIdNum = typeof poData.rfq_id === 'string' && poData.rfq_id.startsWith('REQ-')
              ? parseInt(poData.rfq_id.replace('REQ-', ''), 10)
              : parseInt(poData.rfq_id, 10);
            
            if (!isNaN(rfqIdNum)) {
              const rfqResponse = await CommonService.get(`/rfq/${rfqIdNum}`);
              if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                rfqData = rfqResponse.data;
              }
            }
          } catch (rfqError) {
            console.warn('Error fetching RFQ data:', rfqError);
          }
        }

        // Fetch quotation data if available
        let quotationData = null;
        if (poData.quotation_id) {
          try {
            const quotationResponse = await CommonService.get(`/quotation/${poData.quotation_id}`);
            if (quotationResponse && quotationResponse.success && quotationResponse.data) {
              quotationData = quotationResponse.data;
            }
          } catch (quotationError) {
            console.warn('Error fetching quotation data:', quotationError);
          }
        }

        // Format date to YYYY-MM-DD
        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
          } catch (e) {
            return '';
          }
        };

        // Parse address and format with all details (address, city, state, country, pincode)
        const parseAddress = (address) => {
          if (!address) return [];
          
          // If it's already an array, return it
          if (Array.isArray(address)) return address;
          
          // If it's a string, try to parse it
          if (typeof address === 'string') {
            try {
              const parsed = JSON.parse(address);
              // If parsed is an object with address fields, format it
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                const addressLines = [];
                if (parsed.address) addressLines.push(parsed.address);
                if (parsed.landmark) addressLines.push(parsed.landmark);
                if (parsed.city) addressLines.push(parsed.city);
                if (parsed.state) addressLines.push(parsed.state);
                if (parsed.country) addressLines.push(parsed.country);
                if (parsed.pincode) addressLines.push(parsed.pincode);
                return addressLines.filter(line => line);
              }
              // If parsed is an array, return it
              if (Array.isArray(parsed)) return parsed;
            } catch (e) {
              // Not JSON, treat as comma-separated string
              return address.split(',').map(line => line.trim()).filter(line => line);
            }
          }
          
          // If it's an object with address fields, format it
          if (typeof address === 'object' && !Array.isArray(address)) {
            const addressLines = [];
            if (address.address) addressLines.push(address.address);
            if (address.landmark) addressLines.push(address.landmark);
            if (address.city) addressLines.push(address.city);
            if (address.state) addressLines.push(address.state);
            if (address.country) addressLines.push(address.country);
            if (address.pincode) addressLines.push(address.pincode);
            return addressLines.filter(line => line);
          }
          
          return [];
        };

        // Helper function to parse delivery time (e.g., "4 WEEKS" or "30 DAYS")
        const parseDeliveryTime = (deliveryTimeStr) => {
          if (!deliveryTimeStr) return { value: '', unit: 'WEEKS' };
          
          const str = String(deliveryTimeStr).trim().toUpperCase();
          const weeksMatch = str.match(/(\d+)\s*WEEKS?/);
          const daysMatch = str.match(/(\d+)\s*DAYS?/);
          
          if (weeksMatch) {
            return { value: weeksMatch[1], unit: 'WEEKS' };
          } else if (daysMatch) {
            return { value: daysMatch[1], unit: 'DAYS' };
          } else {
            // Try to extract just the number
            const numMatch = str.match(/(\d+)/);
            if (numMatch) {
              return { value: numMatch[1], unit: 'WEEKS' };
            }
          }
          
          return { value: '', unit: 'WEEKS' };
        };

        // Parse delivery time from quotation
        const deliveryTimeInfo = quotationData?.delivery_time 
          ? parseDeliveryTime(quotationData.delivery_time)
          : quotationData?.lead_time_days 
            ? { value: String(quotationData.lead_time_days), unit: 'DAYS' }
            : { value: '', unit: 'WEEKS' };

        // Helper function to find matching currency option and get its value
        const findCurrencyValue = (currencyFromData) => {
          if (!currencyFromData) return '';
          
          // If currencyOptions are available, try to find matching option
          if (currencyOptions && currencyOptions.length > 0) {
            const foundCurrency = currencyOptions.find(opt => 
              (opt.currencyCode && String(opt.currencyCode).toUpperCase() === String(currencyFromData).toUpperCase()) ||
              (opt.value && String(opt.value) === String(currencyFromData)) ||
              (opt.label && String(opt.label).toUpperCase().includes(String(currencyFromData).toUpperCase()))
            );
            if (foundCurrency) {
              // Return the value (currency_id) that matches the dropdown option
              return foundCurrency.value || foundCurrency.currencyCode || currencyFromData;
            }
          }
          
          // Fallback: return the original value
          return currencyFromData;
        };

        // Helper function to find matching payment terms option and get its value
        const findPaymentTermsValue = (paymentTermsFromData) => {
          if (!paymentTermsFromData) return '';
          
          // If paymentScheduleOptions are available, try to find matching option
          if (paymentScheduleOptions && paymentScheduleOptions.length > 0) {
            const foundPaymentTerm = paymentScheduleOptions.find(opt => 
              (opt.value && String(opt.value) === String(paymentTermsFromData)) ||
              (opt.label && String(opt.label).toUpperCase() === String(paymentTermsFromData).toUpperCase()) ||
              (opt.label && String(opt.label).toUpperCase().includes(String(paymentTermsFromData).toUpperCase()))
            );
            if (foundPaymentTerm) {
              // Return the value (payment_schedule_id) that matches the dropdown option
              return foundPaymentTerm.value || foundPaymentTerm.label || paymentTermsFromData;
            }
          }
          
          // Fallback: return the original value
          return paymentTermsFromData;
        };

        // Helper function to find matching incoterms option and get its value
        const findIncotermsValue = (incotermsFromData) => {
          if (!incotermsFromData) return '';
          
          // If shippingTermsOptions are available, try to find matching option
          if (shippingTermsOptions && shippingTermsOptions.length > 0) {
            const foundIncoterm = shippingTermsOptions.find(opt => 
              (opt.value && String(opt.value) === String(incotermsFromData)) ||
              (opt.label && String(opt.label).toUpperCase() === String(incotermsFromData).toUpperCase()) ||
              (opt.label && String(opt.label).toUpperCase().includes(String(incotermsFromData).toUpperCase()))
            );
            if (foundIncoterm) {
              // Return the value (incoterm_id) that matches the dropdown option
              return foundIncoterm.value || foundIncoterm.label || incotermsFromData;
            }
          }
          
          // Fallback: return the original value
          return incotermsFromData;
        };

        // Get currency value from invoice, PO or quotation, matching with dropdown options
        const currencyValue = findCurrencyValue(
          invoiceData?.currency || poData.currency || quotationData?.currency || 'INR'
        );
        
        // Get payment terms value from invoice, PO or quotation, matching with dropdown options
        const paymentTermsValue = findPaymentTermsValue(
          invoiceData?.payment_terms || poData.payment_terms || quotationData?.payment_terms?.[0]?.value || ''
        );
        
        // Get incoterms value from invoice, PO or quotation, matching with dropdown options
        const incotermsFromData = invoiceData?.trade_terms || poData.incoterms || quotationData?.incoterms || '';
        const incotermsValue = findIncotermsValue(incotermsFromData);
        
        // Store initial value for later matching if options aren't loaded yet
        if (incotermsFromData) {
          initialIncotermsRef.current = incotermsFromData;
        }

        // Helper function to find matching shipping method option and get its value
        const findShippingMethodValue = (shippingMethodFromData) => {
          if (!shippingMethodFromData) return '';
          
          // If modeOfTransportOptions are available, try to find matching option
          if (modeOfTransportOptions && modeOfTransportOptions.length > 0) {
            const foundMethod = modeOfTransportOptions.find(opt => 
              (opt.value && String(opt.value) === String(shippingMethodFromData)) ||
              (opt.label && String(opt.label).toUpperCase() === String(shippingMethodFromData).toUpperCase()) ||
              (opt.label && String(opt.label).toUpperCase().includes(String(shippingMethodFromData).toUpperCase()))
            );
            if (foundMethod) {
              // Return the value (shipping_info_id) that matches the dropdown option
              return foundMethod.value || foundMethod.label || shippingMethodFromData;
            }
          }
          
          // Fallback: try to match with hardcoded options
          const hardcodedOptions = ['Sea Freight', 'Air Freight', 'Road Transport', 'Rail Transport'];
          const matchedOption = hardcodedOptions.find(opt => 
            opt.toUpperCase() === String(shippingMethodFromData).toUpperCase() ||
            String(shippingMethodFromData).toUpperCase().includes(opt.toUpperCase())
          );
          if (matchedOption) {
            return matchedOption;
          }
          
          // Fallback: return the original value
          return shippingMethodFromData;
        };

        // Helper function to find matching country option and get its value
        const findCountryValue = (countryFromData) => {
          if (!countryFromData) return '';
          
          // If countries are available, try to find matching option
          if (countries && countries.length > 0) {
            const foundCountry = countries.find(opt => 
              (opt.id && String(opt.id) === String(countryFromData)) ||
              (opt.value && String(opt.value) === String(countryFromData)) ||
              (opt.label && String(opt.label).toUpperCase() === String(countryFromData).toUpperCase()) ||
              (opt.name && String(opt.name).toUpperCase() === String(countryFromData).toUpperCase())
            );
            if (foundCountry) {
              // Return the value (country_id) that matches the dropdown option
              return foundCountry.id || foundCountry.value || countryFromData;
            }
          }
          
          // Fallback: return the original value
          return countryFromData;
        };

        // Helper function to find matching state option and get its value
        const findStateValue = (stateFromData) => {
          if (!stateFromData) return '';
          
          // If states are available, try to find matching option
          if (states && states.length > 0) {
            const foundState = states.find(opt => 
              (opt.id && String(opt.id) === String(stateFromData)) ||
              (opt.value && String(opt.value) === String(stateFromData)) ||
              (opt.label && String(opt.label).toUpperCase() === String(stateFromData).toUpperCase()) ||
              (opt.name && String(opt.name).toUpperCase() === String(stateFromData).toUpperCase())
            );
            if (foundState) {
              // Return the value (state_id) that matches the dropdown option
              return foundState.id || foundState.value || stateFromData;
            }
          }
          
          // Fallback: return the original value
          return stateFromData;
        };

        // Helper function to find matching city option and get its value
        const findCityValue = (cityFromData) => {
          if (!cityFromData) return '';
          
          // If cities are available, try to find matching option
          if (cities && cities.length > 0) {
            const foundCity = cities.find(opt => 
              (opt.id && String(opt.id) === String(cityFromData)) ||
              (opt.value && String(opt.value) === String(cityFromData)) ||
              (opt.label && String(opt.label).toUpperCase() === String(cityFromData).toUpperCase()) ||
              (opt.name && String(opt.name).toUpperCase() === String(cityFromData).toUpperCase())
            );
            if (foundCity) {
              // Return the value (city_id) that matches the dropdown option
              return foundCity.id || foundCity.value || cityFromData;
            }
          }
          
          // Fallback: return the original value
          return cityFromData;
        };

        // Get shipping method value from invoice, PO or quotation, matching with dropdown options
        const shippingMethodValue = findShippingMethodValue(
          invoiceData?.shipping_method || poData.freight_terms || quotationData?.shipping_method || ''
        );
        
        // Get country value from invoice or PO, matching with dropdown options
        const shippingCountryValue = findCountryValue(
          invoiceData?.shipping_country || poData.buyer_country || ''
        );
        
        // Get state value from invoice or PO, matching with dropdown options (will be updated when country is set)
        const shippingStateValue = findStateValue(
          invoiceData?.shipping_state || poData.buyer_state || ''
        );
        
        // Get city value from invoice, PO, quotation, or buyer profile (will be updated when state is set)
        // Note: buyer_city and buyer_pincode might not be in PO, so we'll fetch from buyer profile later
        let shippingCityValue = findCityValue(
          invoiceData?.shipping_city || poData.buyer_city || quotationData?.shipping_city || ''
        );
        
        // Get pincode from invoice, PO, quotation, or buyer profile
        let shippingPincodeValue = invoiceData?.shipping_pincode || poData.buyer_pincode || quotationData?.shipping_pincode || '';

        // Store trade type and scenario for conditional rendering
        // Trade type comes from invoice or buyer_purchase_orders.trade_type column
        const currentTradeType = invoiceData?.trade_type || poData.trade_type || poData.tradeType || '';
        const currentTradeScenario = invoiceData?.trade_scenario || poData.trade_scenario || poData.tradeScenario || '';
        
        console.log('Trade Type:', currentTradeType);
        console.log('Trade Scenario:', currentTradeScenario);
        
        setTradeType(currentTradeType);
        setTradeScenario(currentTradeScenario);
        
        // Helper function to check if trade type indicates export
        const isExportTrade = (tradeTypeStr) => {
          if (!tradeTypeStr) return false;
          const lowerTradeType = tradeTypeStr.toLowerCase();
          return lowerTradeType.includes('export') || 
                 lowerTradeType.includes('india → abroad') ||
                 lowerTradeType.includes('india -> abroad') ||
                 lowerTradeType.includes('india → foreign') ||
                 lowerTradeType.includes('in→ export');
        };

        // Set form data from invoice (if available), purchase order and quotation
        setFormData(prev => ({
          ...prev,
          poReference: invoiceData?.po_reference || poData.po_number || '',
          poDate: formatDateForInput(invoiceData?.po_date || poData.po_date),
          invoiceNumber: invoiceData?.invoice_number || prev.invoiceNumber || '',
          invoiceDate: invoiceData?.invoice_date ? formatDateForInput(invoiceData.invoice_date) : prev.invoiceDate || '',
          dueDate: invoiceData?.due_date ? formatDateForInput(invoiceData.due_date) : prev.dueDate || '',
          currency: currencyValue,
          // Determine export type based on invoice, trade_type or export_type
          exportType: (() => {
            // If invoice has export_type, use it
            if (invoiceData?.export_type) {
              if (invoiceData.export_type === 'Without LUT' || invoiceData.export_type === 'Non-LUT - IGST Applicable') {
                return 'Without LUT (IGST Applicable)';
              } else if (invoiceData.export_type === 'With LUT' || invoiceData.export_type === 'LUT (Letter of Undertaking) - 0% IGST') {
                return 'With LUT (0% IGST)';
              }
            }
            
            // Check if trade_type indicates export
            const isExport = isExportTrade(currentTradeType);
            
            // If export_type is explicitly set in PO, use it
            if (poData.export_type) {
              if (poData.export_type === 'Without LUT' || poData.export_type === 'Non-LUT - IGST Applicable') {
                return 'Without LUT (IGST Applicable)';
              } else if (poData.export_type === 'With LUT' || poData.export_type === 'LUT (Letter of Undertaking) - 0% IGST') {
                return 'With LUT (0% IGST)';
              }
            }
            
            // If trade type indicates export, check export_tax_type from quotation
            if (isExport && quotationData?.export_tax_type) {
              if (quotationData.export_tax_type === 'LUT (Letter of Undertaking) - 0% IGST') {
                return 'With LUT (0% IGST)';
              } else if (quotationData.export_tax_type === 'Non-LUT - IGST Applicable') {
                return 'Without LUT (IGST Applicable)';
              }
            }
            
            // Default based on trade type
            if (isExport) {
              // Default to With LUT if export and no explicit type
              return 'With LUT (0% IGST)';
            }
            
            // For non-export trades, return default
            return 'With LUT (0% IGST)';
          })(),
          // LUT Number from invoice, PO lut_arn or quotation arn_number
          lutNumber: invoiceData?.lut_number || poData.lut_arn || quotationData?.arn_number || '',
          // AR Number from invoice, quotation arn_number
          arNumber: invoiceData?.ar_number || quotationData?.arn_number || '',
          // LUT Date and AR Date - format dates if available from invoice, PO/quotation
          lutDate: invoiceData?.lut_date ? formatDateForInput(invoiceData.lut_date) : (poData.lut_date ? formatDateForInput(poData.lut_date) : ''),
          arDate: invoiceData?.ar_date ? formatDateForInput(invoiceData.ar_date) : (poData.ar_date ? formatDateForInput(poData.ar_date) : ''),
          placeOfSupply: invoiceData?.place_of_supply || poData.buyer_country || '',
          paymentTerms: paymentTermsValue,
          paymentDueWithin: invoiceData?.payment_due_within ? String(invoiceData.payment_due_within) : (quotationData?.payment_due_days ? String(quotationData.payment_due_days) : ''),
          advancePayment: invoiceData?.advance_payment ? String(invoiceData.advance_payment).replace('%', '') : (quotationData?.advance_payment ? String(quotationData.advance_payment).replace('%', '') : ''),
          balancePayment: invoiceData?.balance_payment ? String(invoiceData.balance_payment).replace('%', '') : (quotationData?.balance_payment ? String(quotationData.balance_payment).replace('%', '') : ''),
          shippingMethod: shippingMethodValue,
          portOfLoading: invoiceData?.port_of_loading || poData.port_of_loading || '',
          portOfDischarge: invoiceData?.port_of_discharge || poData.port_of_discharge || '',
          shippingAddress: invoiceData?.shipping_address || poData.buyer_address || '',
          shippingCountry: shippingCountryValue,
          shippingState: shippingStateValue,
          shippingCity: shippingCityValue,
          shippingPincode: shippingPincodeValue,
          tradeTerms: incotermsValue,
          deliveryTime: invoiceData?.delivery_time ? String(invoiceData.delivery_time) : deliveryTimeInfo.value,
          deliveryTimeUnit: invoiceData?.delivery_time_unit || deliveryTimeInfo.unit,
          noOfPackages: invoiceData?.no_of_packages ? String(invoiceData.no_of_packages) : '',
          packagingType: invoiceData?.packaging_type || '',
          grossWeight: invoiceData?.gross_weight ? String(invoiceData.gross_weight) : '',
          netWeight: invoiceData?.net_weight ? String(invoiceData.net_weight) : '',
          dimensions: invoiceData?.dimensions || '',
          additionalNotes: invoiceData?.additional_notes || poData.special_instructions || ''
        }));

        // Trigger location list updates if country/state are set
        if (shippingCountryValue) {
          updateListsForCountry(shippingCountryValue);
        }
        if (shippingStateValue && shippingCountryValue) {
          // Wait a bit for states to load, then update cities
          setTimeout(() => {
            // updateListsForState expects (countryId, stateId) - pass both parameters
            updateListsForState(shippingCountryValue, shippingStateValue);
          }, 100);
        }

        // Fetch seller and buyer company profiles in parallel
        let sellerIEC = '';
        let sellerGstinFromProfile = '';
        let sellerEmail = '';
        let sellerPhone = '';
        let sellerAddress = [];
        let buyerEmail = '';
        let buyerPhone = '';
        let buyerAddress = [];
        
        const profilePromises = [];
        
        // Fetch seller company profile
        if (poData.seller_company_id) {
          profilePromises.push(
            CommonService.get(`/company/profile/${poData.seller_company_id}`)
              .then(sellerProfileResponse => {
                if (sellerProfileResponse && sellerProfileResponse.success && sellerProfileResponse.data) {
                  const sellerProfile = sellerProfileResponse.data;
                  sellerIEC = sellerProfile.iec_number || '';
                  sellerGstinFromProfile = sellerProfile.tax_number || '';
                  
                  // Get email and phone from user_login table (included in company profile response)
                  sellerEmail = sellerProfile.user_email || '';
                  sellerPhone = sellerProfile.user_phone || '';
                  
                  // Get address from company profile (structured address object)
                  if (sellerProfile.address) {
                    sellerAddress = parseAddress(sellerProfile.address);
                  }
                  
                  // Fallback to contact_details if user_email/user_phone not available
                  if (!sellerEmail || !sellerPhone) {
                    let contactDetails = sellerProfile.contact_details || {};
                    if (typeof sellerProfile.contact_details === 'string') {
                      try {
                        contactDetails = JSON.parse(sellerProfile.contact_details);
                      } catch (e) {
                        contactDetails = {};
                      }
                    }
                    sellerEmail = sellerEmail || contactDetails.contact_email || contactDetails.alternate_email || '';
                    sellerPhone = sellerPhone || contactDetails.contact_number || contactDetails.alternate_contact_number || '';
                  }
                  
                  console.log('Seller profile fetched:', {
                    companyId: poData.seller_company_id,
                    userLoginId: sellerProfile.user_login_id,
                    iec: sellerIEC,
                    email: sellerEmail,
                    phone: sellerPhone,
                    fullProfile: sellerProfile
                  });
                }
              })
              .catch(profileError => {
                console.error('Error fetching seller company profile:', profileError);
              })
          );
        }
        
        // Variables to store buyer city and pincode from profile
        let buyerCityFromProfile = '';
        let buyerPincodeFromProfile = '';
        
        // Fetch buyer company profile
        if (poData.buyer_company_id) {
          profilePromises.push(
            CommonService.get(`/company/profile/${poData.buyer_company_id}`)
              .then(buyerProfileResponse => {
                if (buyerProfileResponse && buyerProfileResponse.success && buyerProfileResponse.data) {
              const buyerProfile = buyerProfileResponse.data;
              
              // Get email and phone from user_login table (included in company profile response)
              buyerEmail = buyerProfile.user_email || '';
              buyerPhone = buyerProfile.user_phone || '';
              
              // Get address from company profile (structured address object)
              if (buyerProfile.address) {
                buyerAddress = parseAddress(buyerProfile.address);
                
                // Extract city and pincode from address
                if (typeof buyerProfile.address === 'object' && !Array.isArray(buyerProfile.address)) {
                  buyerCityFromProfile = buyerProfile.address.city || '';
                  buyerPincodeFromProfile = buyerProfile.address.pincode || '';
                } else if (typeof buyerProfile.address === 'string') {
                  // Try to parse if it's a JSON string
                  try {
                    const parsedAddress = JSON.parse(buyerProfile.address);
                    if (parsedAddress && typeof parsedAddress === 'object') {
                      buyerCityFromProfile = parsedAddress.city || '';
                      buyerPincodeFromProfile = parsedAddress.pincode || '';
                    }
                  } catch (e) {
                    // Not JSON, ignore
                  }
                }
              }
              
              // Fallback to contact_details if user_email/user_phone not available
              let contactDetails = {};
              if (!buyerEmail || !buyerPhone) {
                contactDetails = buyerProfile.contact_details || {};
                if (typeof buyerProfile.contact_details === 'string') {
                  try {
                    contactDetails = JSON.parse(buyerProfile.contact_details);
                  } catch (e) {
                    contactDetails = {};
                  }
                }
                buyerEmail = buyerEmail || contactDetails.contact_email || contactDetails.alternate_email || '';
                buyerPhone = buyerPhone || contactDetails.contact_number || contactDetails.alternate_contact_number || '';
              }
                  
                  console.log('Buyer profile fetched:', {
                    companyId: poData.buyer_company_id,
                    email: buyerEmail,
                    phone: buyerPhone,
                    city: buyerCityFromProfile,
                    pincode: buyerPincodeFromProfile,
                    contactDetails: contactDetails,
                    fullProfile: buyerProfile
                  });
                }
              })
              .catch(profileError => {
                console.error('Error fetching buyer company profile:', profileError);
              })
          );
        }
        
        // Wait for all profile fetches to complete
        await Promise.all(profilePromises);

        // Update shipping city and pincode if we found them in buyer profile
        let finalShippingCityValue = shippingCityValue;
        let finalShippingPincodeValue = shippingPincodeValue;
        
        if (!finalShippingCityValue && buyerCityFromProfile) {
          finalShippingCityValue = findCityValue(buyerCityFromProfile);
        }
        if (!finalShippingPincodeValue && buyerPincodeFromProfile) {
          finalShippingPincodeValue = buyerPincodeFromProfile;
        }

        // Update form data with city and pincode if they were found
        if (finalShippingCityValue !== shippingCityValue || finalShippingPincodeValue !== shippingPincodeValue) {
          setFormData(prev => ({
            ...prev,
            shippingCity: finalShippingCityValue || prev.shippingCity,
            shippingPincode: finalShippingPincodeValue || prev.shippingPincode
          }));
        }

        // Set seller details from invoice (if available) or PO
        setSellerDetails({
          companyName: invoiceData?.seller_company_name || poData.seller_company_name || '',
          address: invoiceData?.seller_address ? parseAddress(invoiceData.seller_address) : (sellerAddress.length > 0 ? sellerAddress : parseAddress(poData.seller_address)),
          gstin: invoiceData?.seller_gstin || poData.seller_gst_number || sellerGstinFromProfile || '',
          iec: invoiceData?.seller_iec || sellerIEC,
          email: invoiceData?.seller_email || sellerEmail,
          phone: invoiceData?.seller_phone || sellerPhone
        });

        // Set buyer details from invoice (if available) or PO
        setBuyerDetails({
          companyName: invoiceData?.buyer_company_name || poData.buyer_company_name || '',
          address: invoiceData?.buyer_address ? parseAddress(invoiceData.buyer_address) : (buyerAddress.length > 0 ? buyerAddress : parseAddress(poData.buyer_address)),
          taxId: invoiceData?.buyer_tax_id || poData.buyer_gst_number || 'N/A (International)',
          placeOfSupply: invoiceData?.buyer_place_of_supply || poData.buyer_country || '',
          email: invoiceData?.buyer_email || buyerEmail,
          phone: invoiceData?.buyer_phone || buyerPhone
        });

        // Set line items from invoice (if available) or PO
        const invoiceLineItems = invoiceData?.line_items || [];
        const poLineItems = poData.line_items || [];
        const sourceLineItems = invoiceLineItems.length > 0 ? invoiceLineItems : poLineItems;
        
        const transformedLineItems = sourceLineItems.map((item, index) => {
          const quantity = parseFloat(item.quantity) || 0;
          const unitPrice = parseFloat(item.unit_price || item.unitPrice) || 0;
          const discount = parseFloat(item.discount) || 0;
          const subtotal = quantity * unitPrice;
          const discountAmount = subtotal * (discount / 100);
          const taxableAmount = parseFloat(item.taxable_amount || item.taxableAmount) || (subtotal - discountAmount);
          const totalAmount = parseFloat(item.total_amount || item.totalAmount) || taxableAmount;
          
          return {
            id: item.po_line_item_id || item.line_item_id || item.id || index + 1,
            line_item_id: item.line_item_id || item.po_line_item_id || null,
            po_line_item_id:
              invoiceLineItems.length > 0
                ? item.po_line_item_id || null
                : item.po_line_item_id || item.line_item_id || item.id || null,
            quotation_line_item_id: item.quotation_line_item_id || null,
            description: item.description || item.product_name || item.productName || '',
            warranty: item.warranty || '',
            hsnSac: item.hsn_sac || item.hsn_code || item.hsnCode || '',
            countryOfOrigin: item.country_of_origin || item.countryOfOrigin || 'India',
            quantity: quantity,
            unit: item.unit || item.uom || 'pcs',
            unitPrice: unitPrice,
            discount: discount,
            taxableAmount: taxableAmount,
            totalAmount: totalAmount,
            // Tax fields from invoice or PO line items
            cgst: parseFloat(item.cgst || item.cgst_percent || item.cgstPercent || 0),
            sgst: parseFloat(item.sgst || item.sgst_percent || item.sgstPercent || 0),
            igst: parseFloat(item.igst || item.igst_percent || item.igstPercent || 0),
            cess: parseFloat(item.cess || item.cess_percent || item.cessPercent || 0),
            customsDuty: parseFloat(item.customs_duty || item.customsDuty || item.bcd || 0),
            tax: parseFloat(item.tax || item.tax_percent || item.taxPercent || 0),
            // Store percentage values for display
            cgstPercent: parseFloat(item.cgst || item.cgst_percent || item.cgstPercent || 0),
            sgstPercent: parseFloat(item.sgst || item.sgst_percent || item.sgstPercent || 0),
            igstPercent: parseFloat(item.igst || item.igst_percent || item.igstPercent || 0),
            cessPercent: parseFloat(item.cess || item.cess_percent || item.cessPercent || 0)
          };
        });
        setLineItems(transformedLineItems);

        // Set invoice date and number (only if not from invoice data)
        if (!invoiceData) {
          const today = new Date().toISOString().split('T')[0];
          const generatedInvoiceNumber = generateInvoiceNumber();
          setFormData(prev => ({
            ...prev,
            invoiceNumber: prev.invoiceNumber || generatedInvoiceNumber,
            invoiceDate: prev.invoiceDate || today
          }));
        }
        
        // Set bank details from invoice if available
        if (invoiceData) {
          setBankDetails({
            bankName1: invoiceData.bank_name_1 || '',
            accountName1: invoiceData.account_name_1 || '',
            accountNumber1: invoiceData.account_number_1 || '',
            swiftIfsc1: invoiceData.swift_ifsc_1 || '',
            iban1: invoiceData.iban_1 || '',
            branch1: invoiceData.branch_1 || '',
            bankName2: invoiceData.bank_name_2 || '',
            accountName2: invoiceData.account_name_2 || '',
            accountNumber2: invoiceData.account_number_2 || '',
            swiftIfsc2: invoiceData.swift_ifsc_2 || '',
            iban2: invoiceData.iban_2 || '',
            branch2: invoiceData.branch_2 || ''
          });
        }

      } catch (error) {
        console.error('Error fetching purchase order data:', error);
        toast.error('Failed to load purchase order data');
        navigate('/invoice-list');
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrderData();
  }, [location.state, navigate]);

  // Update currency field when currencyOptions are loaded
  useEffect(() => {
    if (currencyOptions && currencyOptions.length > 0 && formData.currency) {
      // Check if current currency value matches any option
      const currentCurrency = formData.currency;
      const foundCurrency = currencyOptions.find(opt => 
        (opt.currencyCode && String(opt.currencyCode).toUpperCase() === String(currentCurrency).toUpperCase()) ||
        (opt.value && String(opt.value) === String(currentCurrency)) ||
        (opt.label && String(opt.label).toUpperCase().includes(String(currentCurrency).toUpperCase()))
      );
      
      // If we found a match and the value doesn't match, update it
      if (foundCurrency) {
        const correctValue = foundCurrency.value || foundCurrency.currencyCode;
        if (String(correctValue) !== String(currentCurrency)) {
          setFormData(prev => ({
            ...prev,
            currency: correctValue
          }));
        }
      }
    }
  }, [currencyOptions, formData.currency]);

  // Update payment terms field when paymentScheduleOptions are loaded
  useEffect(() => {
    if (paymentScheduleOptions && paymentScheduleOptions.length > 0 && formData.paymentTerms) {
      // Check if current payment terms value matches any option
      const currentPaymentTerms = formData.paymentTerms;
      const foundPaymentTerm = paymentScheduleOptions.find(opt => 
        (opt.value && String(opt.value) === String(currentPaymentTerms)) ||
        (opt.label && String(opt.label).toUpperCase() === String(currentPaymentTerms).toUpperCase()) ||
        (opt.label && String(opt.label).toUpperCase().includes(String(currentPaymentTerms).toUpperCase()))
      );
      
      // If we found a match and the value doesn't match, update it
      if (foundPaymentTerm) {
        const correctValue = foundPaymentTerm.value || foundPaymentTerm.label;
        if (String(correctValue) !== String(currentPaymentTerms)) {
          setFormData(prev => ({
            ...prev,
            paymentTerms: correctValue
          }));
        }
      }
    }
  }, [paymentScheduleOptions, formData.paymentTerms]);

  // Track last incoterms value to prevent infinite loops
  const lastIncotermsRef = React.useRef(null);

  // Store initial incoterms value from PO/quotation for later matching
  const initialIncotermsRef = React.useRef(null);

  // Update trade terms (incoterms) field when shippingTermsOptions are loaded
  useEffect(() => {
    if (shippingTermsOptions && shippingTermsOptions.length > 0) {
      // Get the value to match - use formData.tradeTerms if set, otherwise use initial value
      const valueToMatch = formData.tradeTerms || initialIncotermsRef.current;
      
      if (!valueToMatch) return;
      
      // Skip if we've already processed this value
      if (lastIncotermsRef.current === valueToMatch) {
        return;
      }
      
      // Try to find matching incoterm with more flexible matching
      const foundIncoterm = shippingTermsOptions.find(opt => {
        const optValue = String(opt.value || '');
        const optLabel = String(opt.label || '').toUpperCase();
        const matchValue = String(valueToMatch).toUpperCase();
        
        return (
          optValue === String(valueToMatch) ||
          optLabel === matchValue ||
          optLabel.includes(matchValue) ||
          matchValue.includes(optLabel) ||
          // Try matching common incoterm patterns
          (matchValue.includes('FOB') && optLabel.includes('FOB')) ||
          (matchValue.includes('CIF') && optLabel.includes('CIF')) ||
          (matchValue.includes('EXW') && optLabel.includes('EXW')) ||
          (matchValue.includes('DDP') && optLabel.includes('DDP')) ||
          (matchValue.includes('DAP') && optLabel.includes('DAP'))
        );
      });
      
      // If we found a match
      if (foundIncoterm) {
        const correctValue = foundIncoterm.value || foundIncoterm.label;
        if (String(correctValue) !== String(valueToMatch)) {
          lastIncotermsRef.current = correctValue;
          setFormData(prev => ({
            ...prev,
            tradeTerms: correctValue
          }));
        } else {
          lastIncotermsRef.current = valueToMatch;
        }
      } else {
        // If no match found, mark as processed to avoid infinite loop
        lastIncotermsRef.current = valueToMatch;
      }
    }
  }, [shippingTermsOptions, formData.tradeTerms]);

  // Update shipping method field when modeOfTransportOptions are loaded
  useEffect(() => {
    if (modeOfTransportOptions && modeOfTransportOptions.length > 0 && formData.shippingMethod) {
      // Check if current shipping method value matches any option
      const currentShippingMethod = formData.shippingMethod;
      const foundMethod = modeOfTransportOptions.find(opt => 
        (opt.value && String(opt.value) === String(currentShippingMethod)) ||
        (opt.label && String(opt.label).toUpperCase() === String(currentShippingMethod).toUpperCase()) ||
        (opt.label && String(opt.label).toUpperCase().includes(String(currentShippingMethod).toUpperCase()))
      );
      
      // If we found a match and the value doesn't match, update it
      if (foundMethod) {
        const correctValue = foundMethod.value || foundMethod.label;
        if (String(correctValue) !== String(currentShippingMethod)) {
          setFormData(prev => ({
            ...prev,
            shippingMethod: correctValue
          }));
        }
      }
    }
  }, [modeOfTransportOptions, formData.shippingMethod]);

  // Track last country/state values to prevent infinite loops
  const lastCountryRef = React.useRef(null);
  const lastStateRef = React.useRef(null);

  // Update shipping country field when countries are loaded and trigger state list update
  useEffect(() => {
    if (countries && countries.length > 0 && formData.shippingCountry) {
      // Check if current country value matches any option
      const currentCountry = formData.shippingCountry;
      const foundCountry = countries.find(opt => 
        (opt.id && String(opt.id) === String(currentCountry)) ||
        (opt.value && String(opt.value) === String(currentCountry)) ||
        (opt.label && String(opt.label).toUpperCase() === String(currentCountry).toUpperCase()) ||
        (opt.name && String(opt.name).toUpperCase() === String(currentCountry).toUpperCase())
      );
      
      // If we found a match and the value doesn't match, update it
      if (foundCountry) {
        const correctValue = foundCountry.id || foundCountry.value;
        if (String(correctValue) !== String(currentCountry)) {
          setFormData(prev => ({
            ...prev,
            shippingCountry: correctValue
          }));
        }
        // Only update states list if country value has changed
        if (lastCountryRef.current !== correctValue) {
          lastCountryRef.current = correctValue;
          updateListsForCountry(correctValue);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, formData.shippingCountry]);

  // Update shipping state field when states are loaded and trigger city list update
  useEffect(() => {
    if (states && states.length > 0 && formData.shippingState) {
      // Check if current state value matches any option
      const currentState = formData.shippingState;
      const foundState = states.find(opt => 
        (opt.id && String(opt.id) === String(currentState)) ||
        (opt.value && String(opt.value) === String(currentState)) ||
        (opt.label && String(opt.label).toUpperCase() === String(currentState).toUpperCase()) ||
        (opt.name && String(opt.name).toUpperCase() === String(currentState).toUpperCase())
      );
      
      // If we found a match and the value doesn't match, update it
      if (foundState) {
        const correctValue = foundState.id || foundState.value;
        if (String(correctValue) !== String(currentState)) {
          setFormData(prev => ({
            ...prev,
            shippingState: correctValue
          }));
        }
        // Always update cities list when state is set (even if it's the same value, to ensure cities are loaded)
        // Also fetch if cities array is empty or hasn't been fetched for this state
        if (lastStateRef.current !== correctValue || !cities || cities.length === 0) {
          lastStateRef.current = correctValue;
          // updateListsForState expects (countryId, stateId) - pass shippingCountry and stateId
          updateListsForState(formData.shippingCountry || '', correctValue);
        }
      } else {
        // State value not found in states list, but still try to fetch cities
        // This handles cases where state might be set but not in the dropdown yet
        if (lastStateRef.current !== currentState) {
          lastStateRef.current = currentState;
          updateListsForState(formData.shippingCountry || '', currentState);
        }
      }
    } else if (formData.shippingState && states && states.length > 0) {
      // If state is set but states just loaded, fetch cities
      const stateValue = formData.shippingState;
      if (lastStateRef.current !== stateValue || !cities || cities.length === 0) {
        lastStateRef.current = stateValue;
        updateListsForState(formData.shippingCountry || '', stateValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, formData.shippingState]);

  // Update shipping city field when cities are loaded
  useEffect(() => {
    if (cities && cities.length > 0 && formData.shippingCity) {
      // Check if current city value matches any option
      const currentCity = formData.shippingCity;
      const foundCity = cities.find(opt => 
        (opt.id && String(opt.id) === String(currentCity)) ||
        (opt.value && String(opt.value) === String(currentCity)) ||
        (opt.label && String(opt.label).toUpperCase() === String(currentCity).toUpperCase()) ||
        (opt.name && String(opt.name).toUpperCase() === String(currentCity).toUpperCase())
      );
      
      // If we found a match and the value doesn't match, update it
      if (foundCity) {
        const correctValue = foundCity.id || foundCity.value;
        if (String(correctValue) !== String(currentCity)) {
          setFormData(prev => ({
            ...prev,
            shippingCity: correctValue
          }));
        }
      }
    }
  }, [cities, formData.shippingCity]);

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      return sum + (qty * price);
    }, 0);
    const totalDiscount = lineItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      const disc = parseFloat(item.discount) || 0;
      const itemSubtotal = qty * price;
      return sum + (itemSubtotal * (disc / 100));
    }, 0);
    const fobValue = subtotal - totalDiscount;
    
    // Calculate tax amounts based on trade type
    let cgst = 0;
    let sgst = 0;
    let igst = 0;
    let cess = 0;
    let customsDuty = 0;
    let tax = 0;
    
    lineItems.forEach(item => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      const disc = parseFloat(item.discount) || 0;
      const itemSubtotal = qty * price;
      const discountAmount = itemSubtotal * (disc / 100);
      const taxableAmount = itemSubtotal - discountAmount;
      
      if (isIntraState) {
        const cgstPercent = parseFloat(item.cgst ?? item.cgstPercent ?? 0);
        const sgstPercent = parseFloat(item.sgst ?? item.sgstPercent ?? 0);
        const cessPercent = parseFloat(item.cess ?? item.cessPercent ?? 0);
        cgst += taxableAmount * (cgstPercent / 100);
        sgst += taxableAmount * (sgstPercent / 100);
        cess += taxableAmount * (cessPercent / 100);
      } else if (isImport) {
        const customsDutyPercent = parseFloat(item.customsDuty ?? item.bcd ?? 0);
        const igstPercent = parseFloat(item.igst ?? item.igstPercent ?? 0);
        const cessPercent = parseFloat(item.cess ?? item.cessPercent ?? 0);
        customsDuty += taxableAmount * (customsDutyPercent / 100);
        const amountAfterCustoms = taxableAmount + (taxableAmount * (customsDutyPercent / 100));
        igst += amountAfterCustoms * (igstPercent / 100);
        cess += amountAfterCustoms * (cessPercent / 100);
      } else if (isThirdCountry) {
        const taxPercent = parseFloat(item.tax ?? item.taxPercent ?? 0);
        tax += taxableAmount * (taxPercent / 100);
      } else {
        // Inter-State or Export
        if (isExport && formData.exportType === 'With LUT (0% IGST)') {
          igst = 0; // 0% under LUT
        } else {
          const igstPercent = parseFloat(item.igst ?? item.igstPercent ?? 0);
          igst += taxableAmount * (igstPercent / 100);
        }
        const cessPercent = parseFloat(item.cess ?? item.cessPercent ?? 0);
        cess += taxableAmount * (cessPercent / 100);
      }
    });
    
    const totalTax = cgst + sgst + igst + cess + customsDuty + tax;
    const grandTotal = fobValue + totalTax;
    
    return { 
      subtotal, 
      totalDiscount, 
      fobValue, 
      cgst, 
      sgst, 
      igst, 
      cess, 
      customsDuty, 
      tax, 
      totalTax,
      grandTotal 
    };
  };

  const { subtotal, totalDiscount, fobValue, cgst, sgst, igst, cess, customsDuty, tax, totalTax, grandTotal } = calculateTotals();

  // Convert number to words (simplified version)
  const numberToWords = (num) => {
    // This is a placeholder - you would implement a proper number to words converter
    return 'To be calculated based on grand total.';
  };

  // Generate Invoice Number (similar to PO number generation)
  const generateInvoiceNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    
    // Generate a unique 6-digit number from timestamp
    // Use the last 6 digits of the timestamp (milliseconds since epoch)
    const timestamp = now.getTime();
    const sequence = String(timestamp).slice(-6);
    
    return `INV-${year}-${sequence}`;
  };

  // Get currency symbol based on selected currency
  const getCurrencySymbol = () => {
    const selectedCurrency = formData.currency || 'INR';
    let currencySymbol = '₹'; // Default to Indian Rupee
    
    if (currencyOptions && currencyOptions.length > 0) {
      const currencyOption = currencyOptions.find(opt => {
        // Match by value, currencyCode, or label
        return String(opt.value) === String(selectedCurrency) ||
               String(opt.currencyCode) === String(selectedCurrency) ||
               opt.label?.toUpperCase().includes(String(selectedCurrency).toUpperCase());
      });
      
      if (currencyOption && currencyOption.currencySymbol) {
        currencySymbol = currencyOption.currencySymbol;
      }
    }
    
    return currencySymbol;
  };

  // Format currency with symbol based on selected currency
  const formatCurrencySymbol = (amount) => {
    // Get selected currency from formData
    const selectedCurrency = formData.currency || 'INR';
    
    // Find currency option to get symbol
    let currencySymbol = getCurrencySymbol();
    let locale = 'en-IN'; // Default locale
    
    if (currencyOptions && currencyOptions.length > 0) {
      const currencyOption = currencyOptions.find(opt => {
        // Match by value, currencyCode, or label
        return String(opt.value) === String(selectedCurrency) ||
               String(opt.currencyCode) === String(selectedCurrency) ||
               opt.label?.toUpperCase().includes(String(selectedCurrency).toUpperCase());
      });
      
      if (currencyOption) {
        // Adjust locale based on currency code
        const currencyCode = currencyOption.currencyCode || selectedCurrency;
        if (currencyCode === 'USD') {
          locale = 'en-US';
        } else if (currencyCode === 'EUR') {
          locale = 'de-DE'; // German locale for EUR formatting
        } else if (currencyCode === 'GBP') {
          locale = 'en-GB';
        } else if (currencyCode === 'JPY') {
          locale = 'ja-JP';
        } else if (currencyCode === 'CNY') {
          locale = 'zh-CN';
        } else {
          locale = 'en-IN'; // Default to Indian formatting
        }
      }
    }
    
    // Format number based on locale
    const formattedAmount = amount.toLocaleString(locale, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    
    // For some currencies, symbol comes after the amount
    if (currencySymbol === '€' || currencySymbol === '£') {
      return `${formattedAmount} ${currencySymbol}`;
    } else {
      return `${currencySymbol}${formattedAmount}`;
    }
  };

  // Helper functions to determine trade type and tax columns
  const isInternationalTrade = (scenario) => {
    if (!scenario) return false;
    return scenario.includes('Import') || 
           scenario.includes('Export') || 
           scenario.includes('Third Country') ||
           scenario.includes('→IN') ||
           scenario.includes('IN→') ||
           scenario.includes('Abroad');
  };

  const isIntraStateTrade = (scenario, tradeTypeStr) => {
    if (!scenario && !tradeTypeStr) return false;
    const scenarioLower = (scenario || '').toLowerCase();
    const tradeTypeLower = (tradeTypeStr || '').toLowerCase();
    return scenarioLower.includes('intra-state') || 
           scenarioLower.includes('same state') ||
           tradeTypeLower.includes('cgst') ||
           tradeTypeLower.includes('intra-state');
  };

  const isInterStateTrade = (scenario, tradeTypeStr) => {
    if (!scenario && !tradeTypeStr) return false;
    const scenarioLower = (scenario || '').toLowerCase();
    const tradeTypeLower = (tradeTypeStr || '').toLowerCase();
    return (scenarioLower.includes('inter-state') || 
            scenarioLower.includes('between indian states') ||
            tradeTypeLower.includes('igst') ||
            tradeTypeLower.includes('inter-state')) &&
           !isIntraStateTrade(scenario, tradeTypeStr);
  };

  const isExportTrade = (scenario, tradeTypeStr) => {
    if (!scenario && !tradeTypeStr) return false;
    const scenarioLower = (scenario || '').toLowerCase();
    const tradeTypeLower = (tradeTypeStr || '').toLowerCase();
    return scenarioLower.includes('export') || 
           scenarioLower.includes('india → abroad') ||
           scenarioLower.includes('india -> abroad') ||
           tradeTypeLower.includes('export');
  };

  const isImportTrade = (scenario, tradeTypeStr) => {
    if (!scenario && !tradeTypeStr) return false;
    const scenarioLower = (scenario || '').toLowerCase();
    const tradeTypeLower = (tradeTypeStr || '').toLowerCase();
    return scenarioLower.includes('import') || 
           scenarioLower.includes('abroad → india') ||
           tradeTypeLower.includes('import');
  };

  const isThirdCountryTrade = (scenario, tradeTypeStr) => {
    if (!scenario && !tradeTypeStr) return false;
    const scenarioLower = (scenario || '').toLowerCase();
    const tradeTypeLower = (tradeTypeStr || '').toLowerCase();
    return scenarioLower.includes('third country') || 
           scenarioLower.includes('abroad → abroad') ||
           tradeTypeLower.includes('third country');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSellerChange = (e) => {
    const { name, value } = e.target;
    setSellerDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleBuyerChange = (e) => {
    const { name, value } = e.target;
    setBuyerDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleLineItemChange = (id, field, value, additionalFields = {}) => {
    setLineItems(prev => prev.map(item => {
      if (item.id === id) {
        const numValue = field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'cgst' || field === 'sgst' || field === 'igst' || field === 'cess' || field === 'customsDuty' || field === 'tax' ? parseFloat(value) || 0 : value;
        const updated = { ...item, [field]: numValue, ...additionalFields };
        
        // Sync tax percentage fields when tax fields are updated
        if (field === 'cgst') {
          updated.cgstPercent = numValue;
        } else if (field === 'sgst') {
          updated.sgstPercent = numValue;
        } else if (field === 'igst') {
          updated.igstPercent = numValue;
        } else if (field === 'cess') {
          updated.cessPercent = numValue;
        } else if (field === 'tax') {
          updated.taxPercent = numValue;
        }
        
        // Recalculate amounts
        if (field === 'quantity' || field === 'unitPrice' || field === 'discount') {
          const qty = parseFloat(updated.quantity) || 0;
          const price = parseFloat(updated.unitPrice) || 0;
          const disc = parseFloat(updated.discount) || 0;
          const subtotal = qty * price;
          const discountAmount = subtotal * (disc / 100);
          updated.taxableAmount = subtotal - discountAmount;
          updated.totalAmount = updated.taxableAmount;
        }
        
        return updated;
      }
      return item;
    }));
  };

  const handleAddLineItem = () => {
    const newId = Math.max(...lineItems.map(item => item.id), 0) + 1;
    setLineItems(prev => [...prev, {
      id: newId,
      description: '',
      warranty: '',
      hsnSac: '',
      countryOfOrigin: 'India',
      quantity: 0,
      unit: 'pcs',
      unitPrice: 0,
      discount: 0,
      taxableAmount: 0,
      totalAmount: 0
    }]);
  };

  const handleDeleteLineItem = (id) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  // Validation function for mandatory fields
  const validateMandatoryFields = (isGenerate = false) => {
    const errors = [];
    const isCmFlow = !!(location.state?.cmRfqId || cmRfqIdFromPo);
    
    // Invoice Date
    if (!formData.invoiceDate) {
      errors.push('Invoice date is required');
    }
    
    // Line Items
    if (lineItems.length === 0) {
      errors.push('At least one line item is required');
    } else {
      // Validate each line item
      lineItems.forEach((item, index) => {
        if (!item.description || String(item.description || '').trim() === '') {
          errors.push(`Line item ${index + 1}: Description is required`);
        }
        if (!item.quantity || parseFloat(item.quantity) <= 0) {
          errors.push(`Line item ${index + 1}: Quantity is required and must be greater than 0`);
        }
        if (!item.unitPrice || parseFloat(item.unitPrice) <= 0) {
          errors.push(`Line item ${index + 1}: Unit price is required and must be greater than 0`);
        }
        // HSN/SAC is optional for Contract Manufacturing production flow
        if (!isCmFlow && (!item.hsnSac || String(item.hsnSac || '').trim() === '')) {
          errors.push(`Line item ${index + 1}: HSN/SAC code is required`);
        }
        if (!item.unit || String(item.unit || '').trim() === '') {
          errors.push(`Line item ${index + 1}: Unit of measurement is required`);
        }
      });
    }
    
    // Export Type validations
    if (isExport) {
      if (!formData.exportType) {
        errors.push('Export type is required');
      } else if (formData.exportType === 'With LUT (0% IGST)') {
        if (!formData.lutNumber || String(formData.lutNumber || '').trim() === '') {
          errors.push('LUT Number is required for Export with LUT');
        }
        if (!formData.lutDate) {
          errors.push('LUT Date is required for Export with LUT');
        }
      } else if (formData.exportType === 'Without LUT (IGST Applicable)') {
        if (!formData.arNumber || String(formData.arNumber || '').trim() === '') {
          errors.push('AR Number is required for Export without LUT');
        }
        if (!formData.arDate) {
          errors.push('AR Date is required for Export without LUT');
        }
      }
      
      if (!formData.placeOfSupply || String(formData.placeOfSupply || '').trim() === '') {
        errors.push('Place of Supply is required for Export');
      }
    }
    
    // Payment Terms
    if (!formData.paymentTerms || String(formData.paymentTerms || '').trim() === '') {
      errors.push('Payment Terms is required');
    }
    
    // Shipping & Delivery Information
    if (!formData.shippingMethod || String(formData.shippingMethod || '').trim() === '') {
      errors.push('Shipping Method is required');
    }
    if (!formData.shippingCountry || String(formData.shippingCountry || '').trim() === '') {
      errors.push('Shipping Country is required');
    }
    if (!formData.shippingState || String(formData.shippingState || '').trim() === '') {
      errors.push('Shipping State is required');
    }
    if (!formData.shippingCity || String(formData.shippingCity || '').trim() === '') {
      errors.push('Shipping City is required');
    }
    if (!formData.shippingPincode || String(formData.shippingPincode || '').trim() === '') {
      errors.push('Shipping Pincode is required');
    }
    
    // Trade Terms (Incoterms)
    if (!formData.tradeTerms || String(formData.tradeTerms || '').trim() === '') {
      errors.push('Trade Terms (Incoterms) is required');
    }
    
    // Delivery Time
    if (!formData.deliveryTime || parseFloat(formData.deliveryTime) <= 0) {
      errors.push('Delivery Time is required and must be greater than 0');
    }
    
    // For Generate Invoice, additional validations
    if (isGenerate) {
      // Currency
      if (!formData.currency || String(formData.currency || '').trim() === '') {
        errors.push('Currency is required');
      }
      
      // Seller Details
      if (!sellerDetails.companyName || String(sellerDetails.companyName || '').trim() === '') {
        errors.push('Seller Company Name is required');
      }
      if (!sellerDetails.gstin || String(sellerDetails.gstin || '').trim() === '') {
        errors.push('Seller GSTIN is required');
      }
      
      // Buyer Details
      if (!buyerDetails.companyName || String(buyerDetails.companyName || '').trim() === '') {
        errors.push('Buyer Company Name is required');
      }
    }
    
    return errors;
  };

  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      
      // Validate mandatory fields
      const validationErrors = validateMandatoryFields(false);
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => toast.error(error));
        setLoading(false);
        return;
      }
      
      // Prepare invoice data
      const invoiceData = {
        purchase_order_id: location.state?.poId,
        invoice_number: formData.invoiceNumber,
        invoice_date: formData.invoiceDate,
        due_date: formData.dueDate || null,
        status: 'Draft',
        po_reference: formData.poReference || null,
        po_date: formData.poDate || null,
        currency: formData.currency || 'INR',
        export_type: formData.exportType || null,
        lut_number: formData.lutNumber || null,
        lut_date: formData.lutDate || null,
        ar_number: formData.arNumber || null,
        ar_date: formData.arDate || null,
        place_of_supply: formData.placeOfSupply || null,
        payment_terms: formData.paymentTerms || null,
        payment_due_within: formData.paymentDueWithin || null,
        advance_payment: formData.advancePayment ? parseFloat(formData.advancePayment) : null,
        balance_payment: formData.balancePayment ? parseFloat(formData.balancePayment) : null,
        shipping_method: formData.shippingMethod || null,
        port_of_loading: formData.portOfLoading || null,
        port_of_discharge: formData.portOfDischarge || null,
        shipping_address: formData.shippingAddress || null,
        shipping_country: formData.shippingCountry || null,
        shipping_state: formData.shippingState || null,
        shipping_city: formData.shippingCity || null,
        shipping_pincode: formData.shippingPincode || null,
        trade_terms: formData.tradeTerms || null,
        delivery_time: formData.deliveryTime || null,
        delivery_time_unit: formData.deliveryTimeUnit || null,
        no_of_packages: formData.noOfPackages ? parseInt(formData.noOfPackages) : null,
        packaging_type: formData.packagingType || null,
        gross_weight: formData.grossWeight ? parseFloat(formData.grossWeight) : null,
        net_weight: formData.netWeight ? parseFloat(formData.netWeight) : null,
        dimensions: formData.dimensions || null,
        additional_notes: formData.additionalNotes || null,
        seller_company_name: sellerDetails.companyName || null,
        seller_address: sellerDetails.address || null,
        seller_gstin: sellerDetails.gstin || null,
        seller_iec: sellerDetails.iec || null,
        seller_email: sellerDetails.email || null,
        seller_phone: sellerDetails.phone || null,
        buyer_company_name: buyerDetails.companyName || null,
        buyer_address: buyerDetails.address || null,
        buyer_tax_id: buyerDetails.taxId || null,
        buyer_place_of_supply: buyerDetails.placeOfSupply || null,
        buyer_email: buyerDetails.email || null,
        buyer_phone: buyerDetails.phone || null,
        bank_name_1: bankDetails.bankName1 || null,
        account_name_1: bankDetails.accountName1 || null,
        account_number_1: bankDetails.accountNumber1 || null,
        swift_ifsc_1: bankDetails.swiftIfsc1 || null,
        iban_1: bankDetails.iban1 || null,
        branch_1: bankDetails.branch1 || null,
        bank_name_2: bankDetails.bankName2 || null,
        account_name_2: bankDetails.accountName2 || null,
        account_number_2: bankDetails.accountNumber2 || null,
        swift_ifsc_2: bankDetails.swiftIfsc2 || null,
        iban_2: bankDetails.iban2 || null,
        branch_2: bankDetails.branch2 || null,
        trade_type: tradeType || null,
        trade_scenario: tradeScenario || null,
        subtotal: subtotal,
        total_discount: totalDiscount,
        fob_value: fobValue,
        grand_total: grandTotal,
        line_items: lineItems.map((item, index) => ({
          item_sequence: index + 1,
          po_line_item_id: item.po_line_item_id || null,
          quotation_line_item_id: item.quotation_line_item_id || null,
          description: item.description || '',
          warranty: item.warranty || null,
          hsn_sac: item.hsnSac || null,
          country_of_origin: item.countryOfOrigin || 'India',
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit || 'pcs',
          unit_price: parseFloat(item.unitPrice) || 0,
          discount: parseFloat(item.discount) || 0,
          taxable_amount: parseFloat(item.taxableAmount) || 0,
          total_amount: parseFloat(item.totalAmount) || 0,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          igst: parseFloat(item.igst) || 0,
          cess: parseFloat(item.cess) || 0,
          customs_duty: parseFloat(item.customsDuty) || 0,
          tax: parseFloat(item.tax) || 0
        }))
      };
      
      // Check if user is authenticated before making API call
      const token = StorageService.getToken();
      if (!token) {
        toast.error('Please login to continue');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
        setLoading(false);
        return;
      }

      // Check if we're updating an existing invoice or creating a new one
      const invoiceId = location.state?.invoiceId || existingInvoiceId;
      let response;
      
      if (invoiceId) {
        // Update existing invoice draft - use PUT endpoint
        response = await CommonService.put(`/seller-invoice/draft/${invoiceId}`, invoiceData);
      } else {
        // Create new invoice draft - use POST endpoint
        response = await CommonService.post('/seller-invoice/draft', invoiceData);
      }
      
      if (response && response.success) {
        toast.success('Invoice saved as draft successfully');
        // Navigate to invoice list page after saving draft
        setTimeout(() => {
          navigate('/invoice-list');
        }, 1000);
      } else {
        toast.error(response?.error || 'Failed to save invoice draft');
      }
    } catch (error) {
      console.error('Error saving invoice draft:', error);
      
      // Handle 401 unauthorized errors specifically
      if (error.response?.status === 401) {
        const errorMessage = error.response?.data?.error || 'Unauthorized. Please login again.';
        toast.error(errorMessage);
        // Delay redirect to allow user to see the error message
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        toast.error(error.response?.data?.error || error.message || 'Failed to save invoice draft');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setLoading(true);
      
      // Validate mandatory fields (more strict for generate)
      const validationErrors = validateMandatoryFields(true);
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => toast.error(error));
        setLoading(false);
        return;
      }
      
      // Prepare invoice data
      const invoiceData = {
        purchase_order_id: location.state?.poId,
        invoice_number: formData.invoiceNumber,
        invoice_date: formData.invoiceDate,
        due_date: formData.dueDate || null,
        status: 'Generated',
        po_reference: formData.poReference || null,
        po_date: formData.poDate || null,
        currency: formData.currency || 'INR',
        export_type: formData.exportType || null,
        lut_number: formData.lutNumber || null,
        lut_date: formData.lutDate || null,
        ar_number: formData.arNumber || null,
        ar_date: formData.arDate || null,
        place_of_supply: formData.placeOfSupply || null,
        payment_terms: formData.paymentTerms || null,
        payment_due_within: formData.paymentDueWithin || null,
        advance_payment: formData.advancePayment ? parseFloat(formData.advancePayment) : null,
        balance_payment: formData.balancePayment ? parseFloat(formData.balancePayment) : null,
        shipping_method: formData.shippingMethod || null,
        port_of_loading: formData.portOfLoading || null,
        port_of_discharge: formData.portOfDischarge || null,
        shipping_address: formData.shippingAddress || null,
        shipping_country: formData.shippingCountry || null,
        shipping_state: formData.shippingState || null,
        shipping_city: formData.shippingCity || null,
        shipping_pincode: formData.shippingPincode || null,
        trade_terms: formData.tradeTerms || null,
        delivery_time: formData.deliveryTime || null,
        delivery_time_unit: formData.deliveryTimeUnit || null,
        no_of_packages: formData.noOfPackages ? parseInt(formData.noOfPackages) : null,
        packaging_type: formData.packagingType || null,
        gross_weight: formData.grossWeight ? parseFloat(formData.grossWeight) : null,
        net_weight: formData.netWeight ? parseFloat(formData.netWeight) : null,
        dimensions: formData.dimensions || null,
        additional_notes: formData.additionalNotes || null,
        seller_company_name: sellerDetails.companyName || null,
        seller_address: sellerDetails.address || null,
        seller_gstin: sellerDetails.gstin || null,
        seller_iec: sellerDetails.iec || null,
        seller_email: sellerDetails.email || null,
        seller_phone: sellerDetails.phone || null,
        buyer_company_name: buyerDetails.companyName || null,
        buyer_address: buyerDetails.address || null,
        buyer_tax_id: buyerDetails.taxId || null,
        buyer_place_of_supply: buyerDetails.placeOfSupply || null,
        buyer_email: buyerDetails.email || null,
        buyer_phone: buyerDetails.phone || null,
        bank_name_1: bankDetails.bankName1 || null,
        account_name_1: bankDetails.accountName1 || null,
        account_number_1: bankDetails.accountNumber1 || null,
        swift_ifsc_1: bankDetails.swiftIfsc1 || null,
        iban_1: bankDetails.iban1 || null,
        branch_1: bankDetails.branch1 || null,
        bank_name_2: bankDetails.bankName2 || null,
        account_name_2: bankDetails.accountName2 || null,
        account_number_2: bankDetails.accountNumber2 || null,
        swift_ifsc_2: bankDetails.swiftIfsc2 || null,
        iban_2: bankDetails.iban2 || null,
        branch_2: bankDetails.branch2 || null,
        trade_type: tradeType || null,
        trade_scenario: tradeScenario || null,
        subtotal: subtotal,
        total_discount: totalDiscount,
        fob_value: fobValue,
        grand_total: grandTotal,
        line_items: lineItems.map((item, index) => ({
          item_sequence: index + 1,
          po_line_item_id: item.po_line_item_id || null,
          quotation_line_item_id: item.quotation_line_item_id || null,
          description: item.description || '',
          warranty: item.warranty || null,
          hsn_sac: item.hsnSac || null,
          country_of_origin: item.countryOfOrigin || 'India',
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit || 'pcs',
          unit_price: parseFloat(item.unitPrice) || 0,
          discount: parseFloat(item.discount) || 0,
          taxable_amount: parseFloat(item.taxableAmount) || 0,
          total_amount: parseFloat(item.totalAmount) || 0,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          igst: parseFloat(item.igst) || 0,
          cess: parseFloat(item.cess) || 0,
          customs_duty: parseFloat(item.customsDuty) || 0,
          tax: parseFloat(item.tax) || 0
        }))
      };
      
      // Check if user is authenticated before making API call
      const token = StorageService.getToken();
      if (!token) {
        toast.error('Please login to continue');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
        setLoading(false);
        return;
      }

      // Check if we're updating an existing invoice or creating a new one
      const invoiceId = location.state?.invoiceId || existingInvoiceId;
      let response;
      
      if (invoiceId) {
        // Update existing invoice - use PUT endpoint
        response = await CommonService.put(`/seller-invoice/generate/${invoiceId}`, invoiceData);
      } else {
        // Create new invoice - use POST endpoint
        response = await CommonService.post('/seller-invoice/generate', invoiceData);
      }
      
      if (response && response.success) {
        const approvalStatus = response.data?.buyer_approval_status || 'not_required';
        const skipReason = response.data?.approval_skip_reason;
        const approvalRequestId = response.data?.approval_request_id;
        const approvalPending = response.data?.buyer_approval_pending === true;
        if (approvalPending || approvalRequestId || approvalStatus === 'pending') {
          toast.success(
            'Invoice generated and sent. Buyer internal approval is required before acceptance.',
            { autoClose: 6000 },
          );
        } else if (skipReason) {
          toast.warning(
            `Invoice sent to buyer, but approval was not triggered: ${skipReason}`,
            { autoClose: 9000 },
          );
        } else if (approvalStatus === 'not_required') {
          toast.warning(
            'Invoice sent to buyer. No matching Procurement Approval policy was found — buyer can accept immediately. Configure Purchase request under P2P → Approval Setup → Procurement Approval.',
            { autoClose: 9000 },
          );
        } else {
          toast.success(
            'Invoice generated and sent directly to the buyer for acceptance.',
            { autoClose: 5000 },
          );
        }
        // CM flow: auto-complete the production stage and return to CM RFQs
        const cmRfqId = location.state?.cmRfqId || cmRfqIdFromPo;
        if (cmRfqId) {
          try {
            const id = Number(cmRfqId);
            const url = API_URLS.CONTRACT_MANUFACTURER.SELLER_PRODUCTION_PROCESS_COMPLETE_STAGE(id);
            // Small retry: invoice write + list join can be slightly behind
            let lastErr;
            for (let attempt = 0; attempt < 3; attempt++) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await CommonService.post(url, {});
                lastErr = null;
                break;
              } catch (e) {
                lastErr = e;
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 600));
              }
            }
            if (lastErr) throw lastErr;
          } catch (e) {
            // If this fails, the user can still complete it from the CM RFQ list
            toast.warning(e?.response?.data?.message || e?.message || 'Invoice saved, but CM stage could not be auto-updated');
          }
          navigate('/seller/cm-rfqs');
        } else {
          // Navigate to invoice list page after generating invoice
          navigate('/invoice-list');
        }
      } else {
        toast.error(response?.error || 'Failed to generate and send invoice');
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      
      // Handle 401 unauthorized errors specifically
      if (error.response?.status === 401) {
        const errorMessage = error.response?.data?.error || 'Unauthorized. Please login again.';
        toast.error(errorMessage);
        // Delay redirect to allow user to see the error message
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        toast.error(error.response?.data?.error || error.message || 'Failed to generate and send invoice');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportTypeOptions = ['With LUT (0% IGST)', 'Without LUT (IGST Applicable)'];
  const shippingMethodOptions = ['Sea Freight', 'Air Freight', 'Road Transport', 'Rail Transport'];
  
  const countryOptions = [
    'United States',
    'China',
    'Japan',
    'Germany',
    'United Kingdom',
    'France',
    'India',
    'South Korea',
    'Italy',
    'Canada',
    'Australia',
    'Brazil',
    'Mexico',
    'Spain',
    'Netherlands',
    'Russia',
    'Switzerland',
    'Belgium',
    'Sweden',
    'Poland',
    'Other'
  ];

  const stateOptions = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Other'
  ];
  
  // Use incoterms from API (shippingTermsOptions from useFactoryDetailsMaster)
  const incotermsOptions = shippingTermsOptions || [];
  
  const deliveryTimeUnitOptions = [
    { value: 'WEEKS', label: 'weeks' },
    { value: 'DAYS', label: 'days' },
    { value: 'MONTHS', label: 'months' }
  ];
  const packagingTypeOptions = ['Cartons', 'Pallets', 'Crates', 'Bags', 'Drums', 'Other'];
  // Use payment schedule from API if available, otherwise use hardcoded fallback
  const paymentTermsOptions = paymentScheduleOptions && paymentScheduleOptions.length > 0 
    ? paymentScheduleOptions
    : [
        { value: 'Net 30', label: 'Net 30' },
        { value: 'Net 60', label: 'Net 60' },
        { value: 'Net 90', label: 'Net 90' },
        { value: 'Due on Receipt', label: 'Due on Receipt' },
        { value: '50% Advance, 50% on Delivery', label: '50% Advance, 50% on Delivery' },
        { value: 'Custom', label: 'Custom' }
      ];

  if (loading) {
    return (
      <div className="generate-invoice">
        <div className="gi-main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <p>Loading purchase order data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="generate-invoice">
      <div className="gi-main-content">
        {(location.state?.cmRfqId || cmRfqIdFromPo) ? (
          <div className="alert alert-info d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3" role="status">
            <span>
              Contract Manufacturing production — linked RFQ <strong>#{location.state?.cmRfqId || cmRfqIdFromPo}</strong>
            </span>
            <Link to="/seller/cm-rfqs" className="btn btn-sm btn-outline-primary">
              Back to CM RFQs
            </Link>
          </div>
        ) : null}
        {/* Header */}
        <div className="gi-header">
          <div className="gi-header-left">
            <nav aria-label="breadcrumb" className="gi-breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard/seller" className="breadcrumb-link">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Generate Invoice
                </li>
              </ol>
            </nav>
            <h1 className="gi-main-title">Create Export Invoice</h1>
            <p className="gi-subtitle">Generate professional export invoice from approved Purchase Order</p>
          </div>
          <div className="gi-header-right">
            <div className="gi-action-buttons">
              <button className="gi-back-btn" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                Back
              </button>
              <button 
                type="button" 
                className="gi-btn gi-btn-draft"
                onClick={handleSaveDraft}
              >
                Save as Draft
              </button>
              <button 
                type="button" 
                className="gi-btn gi-btn-primary"
                onClick={handleGenerateInvoice}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Generate & Send Invoice
              </button>
            </div>
          </div>
        </div>

        {/* Main Card Container */}
        <div className="gi-main-card">
          {/* TAX INVOICE / EXPORT INVOICE Section */}
          <div className="gi-invoice-header-section">
            <div className="gi-invoice-header-content">
              <div className="gi-invoice-header-left">
                <h2 className="gi-section-title">TAX INVOICE / EXPORT INVOICE</h2>
                <p className="gi-invoice-label">Original for Buyer</p>
              </div>
              <div className="gi-invoice-header-right">
                <div className="gi-logo-container">
                  <img 
                    src={platformLogoUrl}
                    alt="TROCANET Logo" 
                    className="gi-company-logo"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Seller and Buyer Details - Side by Side */}
          <div className="gi-seller-buyer-grid">
            {/* Seller / Exporter Details */}
            <div className="gi-seller-section">
              <div className="gi-section-header-with-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                  <path d="M10 6h4"></path>
                  <path d="M10 10h4"></path>
                  <path d="M10 14h4"></path>
                  <path d="M10 18h4"></path>
                </svg>
                <h3 className="gi-section-subtitle">Seller / Exporter Details</h3>
              </div>
              <div className="gi-seller-buyer-content">
                <div className="gi-company-name">{sellerDetails.companyName}</div>
                <div className="gi-address-lines">
                  {Array.isArray(sellerDetails.address) 
                    ? sellerDetails.address.map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))
                    : sellerDetails.address.split(',').map((line, idx) => (
                        <p key={idx}>{line.trim()}</p>
                      ))
                  }
                </div>
                <div className="gi-divider"></div>
                <div className="gi-details-grid-2col">
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">GSTIN:</p>
                    <p className="gi-detail-value">{sellerDetails.gstin}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">IEC:</p>
                    <p className="gi-detail-value">{sellerDetails.iec}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Email:</p>
                    <p className="gi-detail-value">{sellerDetails.email}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Phone:</p>
                    <p className="gi-detail-value">{sellerDetails.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Buyer / Consignee Details */}
            <div className="gi-buyer-section">
              <div className="gi-section-header-with-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                  <path d="M10 6h4"></path>
                  <path d="M10 10h4"></path>
                  <path d="M10 14h4"></path>
                  <path d="M10 18h4"></path>
                </svg>
                <h3 className="gi-section-subtitle">Buyer / Consignee Details</h3>
              </div>
              <div className="gi-seller-buyer-content">
                <div className="gi-company-name">{buyerDetails.companyName}</div>
                <div className="gi-address-lines">
                  {Array.isArray(buyerDetails.address) 
                    ? buyerDetails.address.map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))
                    : buyerDetails.address.split(',').map((line, idx) => (
                        <p key={idx}>{line.trim()}</p>
                      ))
                  }
                </div>
                <div className="gi-divider"></div>
                <div className="gi-details-grid-2col">
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Tax ID:</p>
                    <p className="gi-detail-value">{buyerDetails.taxId}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Place of Supply:</p>
                    <p className="gi-detail-value">{buyerDetails.placeOfSupply}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Email:</p>
                    <p className="gi-detail-value">{buyerDetails.email}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Phone:</p>
                    <p className="gi-detail-value">{buyerDetails.phone}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice and Purchase Order Details */}
          <div className="gi-section gi-invoice-details-section">
            <div className="gi-form-grid-6col">
              <div className="gi-form-field">
                <label>Invoice Number *</label>
                <input
                  type="text"
                  className="input-control"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  placeholder="INV-2024-001"
                />
              </div>
              <div className="gi-form-field">
                <label>Invoice Date *</label>
                <input
                  type="date"
                  className="input-control input-control-date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="gi-form-field">
                <label>Due Date *</label>
                <input
                  type="date"
                  className="input-control input-control-date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="gi-form-field">
                <label>PO Reference</label>
                <input
                  type="text"
                  className="input-control"
                  name="poReference"
                  value={formData.poReference}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
              <div className="gi-form-field">
                <label>PO Date</label>
                <input
                  type="date"
                  className="input-control input-control-date"
                  name="poDate"
                  value={formData.poDate}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
              <div className="gi-form-field">
                <label>Currency *</label>
                <select
                  className="input-control input-control-select"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                >
                  {currencyOptions && currencyOptions.length > 0 ? (
                    currencyOptions.map((opt, idx) => (
                      <option key={`currency-${opt.value || opt.currencyCode}-${idx}`} value={opt.value || opt.currencyCode}>
                        {opt.label}
                    </option>
                    ))
                  ) : (
                    <option value="INR">INR - Indian Rupee</option>
                  )}
                </select>
              </div>
            </div>
          </div>

          {/* Export Type Details - Only show for export trades */}
          {(() => {
            // Check if trade type indicates export
            const isExport = tradeType && (
              tradeType.toLowerCase().includes('export') || 
              tradeType.toLowerCase().includes('india → abroad') ||
              tradeType.toLowerCase().includes('india -> abroad') ||
              tradeType.toLowerCase().includes('india → foreign') ||
              tradeType.toLowerCase().includes('in→ export')
            );
            
            if (!isExport) return null;
            
            return (
          <div className="gi-section gi-export-type-section">
            <div className="gi-form-grid-4col">
              <div className="gi-form-field">
                <label>Export Type *</label>
                <select
                  className="gi-input gi-input-select"
                  name="exportType"
                  value={formData.exportType}
                  onChange={handleInputChange}
                >
                  {exportTypeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              {formData.exportType === 'With LUT (0% IGST)' ? (
                <>
                  <div className="gi-form-field">
                    <label>LUT Number *</label>
                    <input
                      type="text"
                      className="gi-input"
                      name="lutNumber"
                      value={formData.lutNumber}
                      onChange={handleInputChange}
                      placeholder="LUT/2024/001"
                    />
                  </div>
                  <div className="gi-form-field">
                    <label>LUT Date *</label>
                    <input
                      type="date"
                          className="input-control input-control-date"
                      name="lutDate"
                      value={formData.lutDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="gi-form-field">
                    <label>AR Number *</label>
                    <input
                      type="text"
                      className="gi-input"
                      name="arNumber"
                      value={formData.arNumber}
                      onChange={handleInputChange}
                      placeholder="AR Number"
                    />
                  </div>
                  <div className="gi-form-field">
                    <label>AR Date *</label>
                    <input
                      type="date"
                          className="input-control input-control-date"
                      name="arDate"
                      value={formData.arDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </>
              )}
              <div className="gi-form-field">
                <label>Place of Supply *</label>
                <input
                  type="text"
                  className="gi-input"
                  name="placeOfSupply"
                  value={formData.placeOfSupply}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>
            );
          })()}

          {/* Description of Goods Table */}
          {(() => {
            // Trade type comes from buyer_purchase_orders.trade_type column
            // Actual values stored in database:
            // - "Intra-State Trade (CGST + SGST)"
            // - "Inter-State Trade (IGST)"
            // - "Import: CIF + BCD + SWS + IGST"
            // - "Export: 0% IGST (with LUT)" or "Export: IGST Applicable"
            // - "Third Country Trade"
            
            // Trade type flags are now defined at component level (above)
            // Debug: Log trade type for verification
            if (tradeType) {
              console.log('Using trade type from buyer_purchase_orders.trade_type for tax columns:', tradeType, {
                isIntraState,
                isImport,
                isThirdCountry,
                isExport,
                isInterState
              });
            }
            
            // Calculate number of tax columns for colspan calculations
            let taxColumnsCount = 0;
            if (isIntraState) taxColumnsCount = 3; // CGST + SGST + CESS
            else if (isImport) taxColumnsCount = 3; // Customs + IGST + CESS
            else if (isThirdCountry) taxColumnsCount = 1; // Tax
            else taxColumnsCount = 2; // IGST + CESS (Inter-State or Export)
            
            // Base columns: #, Description, HSN, Qty, UOM, Unit Price, Disc %, Total = 7
            const baseColumnsCount = 7;
            const totalColumnsCount = baseColumnsCount + taxColumnsCount;
            
            return (
            <div className="gi-table-wrapper">
              <table className="gi-table">
                <thead>
                  <tr>
                    <th className="gi-table-col-number">#</th>
                    <th className="gi-table-col-description">Description</th>
                    <th>HSN</th>
                    <th className="gi-table-col-qty">Qty</th>
                    <th>UOM</th>
                      <th className="gi-table-col-price">Unit Price ({getCurrencySymbol()})</th>
                    <th className="gi-table-col-disc">Disc %</th>
                      {/* Conditional tax columns based on trade type - matching submit quotation page */}
                      {isIntraState ? (
                        <>
                          <th className="gi-table-col-tax">CGST %</th>
                          <th className="gi-table-col-tax">SGST %</th>
                          <th className="gi-table-col-tax">CESS %</th>
                        </>
                      ) : isImport ? (
                        <>
                          <th className="gi-table-col-tax">Customs %</th>
                    <th className="gi-table-col-tax">IGST %</th>
                    <th className="gi-table-col-tax">CESS %</th>
                        </>
                      ) : isThirdCountry ? (
                        <>
                          <th className="gi-table-col-tax">Tax %</th>
                        </>
                      ) : (
                        <>
                          <th className="gi-table-col-tax">IGST %</th>
                          <th className="gi-table-col-tax">CESS %</th>
                        </>
                      )}
                      <th className="gi-table-col-total-header">Total ({getCurrencySymbol()})</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="gi-table-description">
                          <div className="gi-table-text">{item.description}</div>
                          {item.warranty && (
                            <div className="gi-table-text gi-table-text-warranty">{item.warranty}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="gi-table-text">{item.hsnSac}</div>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input-control"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)}
                          min="0"
                          step="1"
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <select
                          className="input-control input-control-select"
                          value={item.unit}
                          onChange={(e) => handleLineItemChange(item.id, 'unit', e.target.value)}
                        >
                            {attributeUnitOptions && attributeUnitOptions.length > 0 ? (
                              attributeUnitOptions.map((opt, idx) => (
                                <option key={`uom-${opt.value}-${idx}`} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))
                            ) : (
                              <>
                          <option value="pcs">pcs</option>
                          <option value="units">units</option>
                          <option value="kg">kg</option>
                          <option value="m">m</option>
                          <option value="sqm">sqm</option>
                          <option value="l">l</option>
                          <option value="ml">ml</option>
                              </>
                            )}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input-control"
                          value={item.unitPrice}
                          onChange={(e) => handleLineItemChange(item.id, 'unitPrice', e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input-control"
                          value={item.discount}
                          onChange={(e) => handleLineItemChange(item.id, 'discount', e.target.value)}
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="0"
                        />
                      </td>
                        {/* Conditional tax cells based on trade type - matching submit quotation page */}
                        {isIntraState ? (
                          <>
                            {/* For Intra-State: CGST field (editable) */}
                      <td>
                        <input
                          type="number"
                                className="input-control"
                                value={item.cgst ?? item.cgstPercent ?? item.igst ?? item.igstPercent ?? 0}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const numValue = parseFloat(val) || 0;
                                  // For intra-state, update both cgst and igst to maintain consistency
                                  handleLineItemChange(item.id, 'cgst', val, {
                                    igst: numValue,
                                    igstPercent: numValue,
                                    cgstPercent: numValue
                                  });
                                }}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                        />
                      </td>
                            {/* For Intra-State: SGST field (editable) */}
                      <td>
                        <input
                          type="number"
                                className="input-control"
                                value={item.sgst ?? item.sgstPercent ?? 0}
                                onChange={(e) => handleLineItemChange(item.id, 'sgst', e.target.value)}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                        />
                      </td>
                            {/* For Intra-State: CESS field (editable) */}
                            <td>
                              <input
                                type="number"
                                className="input-control"
                                value={item.cess ?? item.cessPercent ?? 0}
                                onChange={(e) => handleLineItemChange(item.id, 'cess', e.target.value)}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                              />
                            </td>
                          </>
                        ) : isImport ? (
                          <>
                            <td>
                              <input
                                type="number"
                                className="input-control"
                                value={item.customsDuty ?? item.bcd ?? 0}
                                onChange={(e) => handleLineItemChange(item.id, 'customsDuty', e.target.value)}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="input-control"
                                value={item.igst ?? item.igstPercent ?? 0}
                                onChange={(e) => handleLineItemChange(item.id, 'igst', e.target.value)}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="input-control"
                                value={item.cess ?? item.cessPercent ?? 0}
                                onChange={(e) => handleLineItemChange(item.id, 'cess', e.target.value)}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                              />
                            </td>
                          </>
                        ) : isThirdCountry ? (
                          <>
                            <td>
                              <input
                                type="number"
                                className="input-control"
                                value={item.tax ?? item.taxPercent ?? 0}
                                onChange={(e) => handleLineItemChange(item.id, 'tax', e.target.value)}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                              />
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              <input
                                type="number"
                                className={`input-control ${isExport && formData.exportType === 'With LUT (0% IGST)' ? 'input-control-tax' : ''}`}
                                value={isExport && formData.exportType === 'With LUT (0% IGST)' ? 0 : (item.igst ?? item.igstPercent ?? 0)}
                                onChange={(e) => {
                                  // Only allow editing if not export with LUT
                                  if (!(isExport && formData.exportType === 'With LUT (0% IGST)')) {
                                    handleLineItemChange(item.id, 'igst', e.target.value);
                                  }
                                }}
                                readOnly={isExport && formData.exportType === 'With LUT (0% IGST)'}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder={isExport && formData.exportType === 'With LUT (0% IGST)' ? "0 (LUT)" : "0"}
                                style={isExport && formData.exportType === 'With LUT (0% IGST)' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className={`input-control ${isExport && formData.exportType === 'With LUT (0% IGST)' ? 'input-control-tax' : ''}`}
                                value={isExport && formData.exportType === 'With LUT (0% IGST)' ? 0 : (item.cess ?? item.cessPercent ?? 0)}
                                onChange={(e) => {
                                  // Only allow editing if not export with LUT
                                  if (!(isExport && formData.exportType === 'With LUT (0% IGST)')) {
                                    handleLineItemChange(item.id, 'cess', e.target.value);
                                  }
                                }}
                                readOnly={isExport && formData.exportType === 'With LUT (0% IGST)'}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder={isExport && formData.exportType === 'With LUT (0% IGST)' ? "0 (LUT)" : "0"}
                                style={isExport && formData.exportType === 'With LUT (0% IGST)' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                              />
                            </td>
                          </>
                        )}
                      <td className="gi-table-col-total-cell">{formatCurrencySymbol(item.totalAmount)}</td>
                    </tr>
                  ))}
                  {/* Summary Rows */}
                  <tr className="gi-table-summary-row">
                    <td colSpan="1"></td>
                    <td colSpan="1" className="gi-table-summary-label">
                      <span className="font-bold">Subtotal (Qty × Unit Price):</span>
                    </td>
                    <td colSpan={totalColumnsCount - 2}></td>
                    <td className="gi-table-summary-value" colSpan="1">
                      <span className="font-semibold">{formatCurrencySymbol(subtotal)}</span>
                    </td>
                  </tr>
                  <tr className="gi-table-summary-row">
                    <td colSpan="1"></td>
                    <td colSpan="1" className="gi-table-summary-label">
                      <span className="font-bold">Total Discount:</span>
                    </td>
                    <td colSpan={totalColumnsCount - 2}></td>
                    <td className="gi-table-summary-value" colSpan="1">
                      - <span className="font-semibold">{formatCurrencySymbol(totalDiscount)}</span>
                    </td>
                  </tr>
                  {isExport && (
                  <tr className="gi-table-summary-row gi-breakdown-blue">
                    <td colSpan="1"></td>
                    <td colSpan="1" className="gi-table-summary-label">
                      <span className="font-bold">FOB Value (Free on Board):</span>
                    </td>
                      <td colSpan={totalColumnsCount - 2}></td>
                    <td className="gi-table-summary-value" colSpan="1">
                        <span className="font-semibold">{formatCurrencySymbol(fobValue)}</span>
                    </td>
                  </tr>
                  )}
                  {/* Tax calculations based on trade type */}
                  {isIntraState ? (
                    <>
                      {cgst > 0 && (
                        <tr className="gi-table-summary-row gi-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="gi-table-summary-label">
                            <span className="font-bold">CGST:</span>
                          </td>
                          <td colSpan={totalColumnsCount - 2}></td>
                          <td className="gi-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrencySymbol(cgst)}</span>
                          </td>
                        </tr>
                      )}
                      {sgst > 0 && (
                        <tr className="gi-table-summary-row gi-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="gi-table-summary-label">
                            <span className="font-bold">SGST:</span>
                          </td>
                          <td colSpan={totalColumnsCount - 2}></td>
                          <td className="gi-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrencySymbol(sgst)}</span>
                          </td>
                        </tr>
                      )}
                      {cess > 0 && (
                        <tr className="gi-table-summary-row gi-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="gi-table-summary-label">
                            <span className="font-bold">CESS:</span>
                          </td>
                          <td colSpan={totalColumnsCount - 2}></td>
                          <td className="gi-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrencySymbol(cess)}</span>
                          </td>
                        </tr>
                      )}
                    </>
                  ) : isImport ? (
                    <>
                      {customsDuty > 0 && (
                        <tr className="gi-table-summary-row gi-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="gi-table-summary-label">
                            <span className="font-bold">Customs Duty:</span>
                          </td>
                          <td colSpan={totalColumnsCount - 2}></td>
                          <td className="gi-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrencySymbol(customsDuty)}</span>
                          </td>
                        </tr>
                      )}
                      {igst > 0 && (
                        <tr className="gi-table-summary-row gi-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="gi-table-summary-label">
                            <span className="font-bold">IGST:</span>
                          </td>
                          <td colSpan={totalColumnsCount - 2}></td>
                          <td className="gi-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrencySymbol(igst)}</span>
                          </td>
                        </tr>
                      )}
                      {cess > 0 && (
                        <tr className="gi-table-summary-row gi-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="gi-table-summary-label">
                            <span className="font-bold">CESS:</span>
                          </td>
                          <td colSpan={totalColumnsCount - 2}></td>
                          <td className="gi-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrencySymbol(cess)}</span>
                          </td>
                        </tr>
                      )}
                    </>
                  ) : isThirdCountry ? (
                    <>
                      {tax > 0 && (
                        <tr className="gi-table-summary-row gi-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="gi-table-summary-label">
                            <span className="font-bold">Tax:</span>
                          </td>
                          <td colSpan={totalColumnsCount - 2}></td>
                          <td className="gi-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrencySymbol(tax)}</span>
                          </td>
                        </tr>
                      )}
                    </>
                  ) : (
                    <>
                      {isExport && formData.exportType === 'With LUT (0% IGST)' ? (
                  <tr className="gi-table-summary-row gi-breakdown-green">
                    <td colSpan="1"></td>
                    <td colSpan="1" className="gi-table-summary-label text-green-700">
                      <span className="font-bold">✓ Export with LUT (Letter of Undertaking) - 0% IGST</span>
                      {formData.lutNumber && (
                        <>
                          <br />
                          <span className="text-xs font-bold">ARN: {formData.lutNumber}</span>
                        </>
                      )}
                    </td>
                          <td colSpan={totalColumnsCount - 2}></td>
                    <td className="gi-table-summary-value text-right text-green-700" colSpan="1">
                      <span className="font-bold">No Tax</span>
                    </td>
                  </tr>
                      ) : (
                        <>
                          {igst > 0 && (
                            <tr className="gi-table-summary-row gi-breakdown-blue">
                              <td colSpan="1"></td>
                              <td colSpan="1" className="gi-table-summary-label">
                                <span className="font-bold">IGST:</span>
                              </td>
                              <td colSpan={totalColumnsCount - 2}></td>
                              <td className="gi-table-summary-value" colSpan="1">
                                <span className="font-semibold">{formatCurrencySymbol(igst)}</span>
                              </td>
                            </tr>
                          )}
                          {cess > 0 && (
                            <tr className="gi-table-summary-row gi-breakdown-blue">
                              <td colSpan="1"></td>
                              <td colSpan="1" className="gi-table-summary-label">
                                <span className="font-bold">CESS:</span>
                              </td>
                              <td colSpan={totalColumnsCount - 2}></td>
                              <td className="gi-table-summary-value" colSpan="1">
                                <span className="font-semibold">{formatCurrencySymbol(cess)}</span>
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </>
                  )}
                  <tr className="gi-table-summary-row gi-table-grand-total gi-breakdown-green-bold">
                    <td colSpan="1"></td>
                    <td colSpan="1" className="gi-table-summary-label">
                      <span className="font-semibold">Grand Total (Sum of Line Items):</span>
                    </td>
                    <td colSpan={totalColumnsCount - 2}></td>
                    <td className="gi-table-summary-value" colSpan="1">
                      <span className="font-semibold">{formatCurrencySymbol(grandTotal)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            );
          })()}
          

          {/* Payment Terms, Delivery Terms, and Bank Details - 2 Column Layout */}
          <div className="gi-payment-amount-grid">
            {/* Left Column - Payment Terms and Delivery Terms */}
            <div className="gi-payment-left-column">
              {/* Payment Terms */}
              <div className="sq-terms-box">
                <h4 className="sq-terms-title">Payment Terms</h4>
                <div className="sq-grid-payment-terms-2col sq-grid-payment-terms-2col-mb">
                  <div className="sq-field">
                    <label>Payment Terms</label>
                    <select
                      className="input-control input-control-select"
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                    >
                      <option value="">Select payment terms</option>
                      {paymentTermsOptions.map((option, idx) => {
                        const value = typeof option === 'string' ? option : (option.value || option.label);
                        const label = typeof option === 'string' ? option : (option.label || option.value);
                        return (
                          <option key={`payment-term-${value}-${idx}`} value={value}>{label}</option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="sq-field">
                    <label>Payment Due Within</label>
                    <input
                      className="input-control"
                      type="number"
                      name="paymentDueWithin"
                      value={formData.paymentDueWithin}
                      onChange={handleInputChange}
                      placeholder="30 days"
                      min="0"
                    />
                  </div>
                </div>
                <div className="sq-grid-payment-terms-2col">
                  <div className="sq-field">
                    <label>Advance Payment (%)</label>
                    <input
                      className="input-control"
                      type="number"
                      name="advancePayment"
                      value={formData.advancePayment}
                      onChange={handleInputChange}
                      placeholder="30%"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="sq-field">
                    <label>Balance Payment (%)</label>
                    <input
                      className="input-control"
                      type="number"
                      name="balancePayment"
                      value={formData.balancePayment}
                      onChange={handleInputChange}
                      placeholder="70%"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Terms */}
              <div className="sq-terms-box sq-mt-16">
                <h4 className="sq-terms-title">Delivery Terms</h4>
                <div className="sq-grid-delivery-terms">
                  <div className="sq-field">
                    <label>Trade Terms (Incoterms)</label>
                    <select
                      className="input-control input-control-select"
                      name="tradeTerms"
                      value={formData.tradeTerms}
                      onChange={handleInputChange}
                    >
                      <option value="" disabled>Select Incoterms</option>
                      {incotermsOptions && incotermsOptions.length > 0 ? (
                        incotermsOptions.map((opt, idx) => (
                          <option key={`incoterm-${opt.value}-${idx}`} value={opt.value || opt.label}>
                            {opt.label}
                          </option>
                        ))
                      ) : (
                        <option value="">Select Incoterms</option>
                      )}
                    </select>
                  </div>
                  <div className="sq-field">
                    <label>Delivery Time</label>
                    <div className="sq-flex-delivery-time">
                      <input
                        className="input-control sq-input-delivery-number"
                        type="number"
                        name="deliveryTime"
                        value={formData.deliveryTime}
                        onChange={handleInputChange}
                        placeholder="4"
                        min="0"
                      />
                      <select
                        className="input-control input-control-select sq-input-delivery-select"
                        name="deliveryTimeUnit"
                        value={formData.deliveryTimeUnit}
                        onChange={handleInputChange}
                      >
                        {deliveryTimeUnitOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Bank Details for Wire Transfer */}
            <div className="gi-payment-right-column">
              {/* Bank Details for Wire Transfer */}
              <div className="sq-terms-box">
                <h4 className="sq-terms-title">Bank Details for Wire Transfer</h4>
                <div className="sq-grid-payment-terms-2col">
                  <div className="sq-field">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      className="input-control"
                      name="bankName1"
                      value={bankDetails.bankName1}
                      onChange={handleBankChange}
                      placeholder="Bank Name"
                    />
                  </div>
                  <div className="sq-field">
                    <label>Account Name</label>
                    <input
                      type="text"
                      className="input-control"
                      name="accountName1"
                      value={bankDetails.accountName1}
                      onChange={handleBankChange}
                      placeholder="Account Name"
                    />
                  </div>
                  <div className="sq-field">
                    <label>Account Number</label>
                    <input
                      type="text"
                      className="input-control"
                      name="accountNumber1"
                      value={bankDetails.accountNumber1}
                      onChange={handleBankChange}
                      placeholder="Account Number"
                    />
                  </div>
                  <div className="sq-field">
                    <label>SWIFT / IFSC Code</label>
                    <input
                      type="text"
                      className="input-control"
                      name="swiftIfsc1"
                      value={bankDetails.swiftIfsc1}
                      onChange={handleBankChange}
                      placeholder="SWIFT/IFSC"
                    />
                  </div>
                  <div className="sq-field">
                    <label>IBAN (if applicable)</label>
                    <input
                      type="text"
                      className="input-control"
                      name="iban1"
                      value={bankDetails.iban1}
                      onChange={handleBankChange}
                      placeholder="IBAN"
                    />
                  </div>
                  <div className="sq-field">
                    <label>Branch</label>
                    <input
                      type="text"
                      className="input-control"
                      name="branch1"
                      value={bankDetails.branch1}
                      onChange={handleBankChange}
                      placeholder="Branch"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping & Delivery Information */}
          <div className="sq-terms-box">
            <h4 className="sq-terms-title">Shipping & Delivery Information</h4>
            <div className="sq-grid-shipping-3col">
              <div className="sq-field">
                <label>Shipping Method</label>
                <select
                  className="input-control input-control-select"
                  name="shippingMethod"
                  value={formData.shippingMethod}
                  onChange={handleInputChange}
                >
                  {shippingMethodOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="sq-field">
                <label>Port of Loading</label>
                <input
                  type="text"
                  className="input-control"
                  name="portOfLoading"
                  value={formData.portOfLoading}
                  onChange={handleInputChange}
                  placeholder="e.g., INMUN - Mumbai"
                />
              </div>
              <div className="sq-field">
                <label>Port of Discharge</label>
                <input
                  type="text"
                  className="input-control"
                  name="portOfDischarge"
                  value={formData.portOfDischarge}
                  onChange={handleInputChange}
                  placeholder="e.g., DEHAM - Hamburg"
                />
              </div>
            </div>
            <div className="sq-field sq-field-mt">
              <label>Shipping Address</label>
              <textarea
                className="input-control"
                name="shippingAddress"
                value={formData.shippingAddress}
                onChange={handleInputChange}
                placeholder="Enter shipping address"
                rows="1"
              />
            </div>
            <div className="sq-grid-shipping-address-4col">
              <div className="sq-field">
                <label>Country</label>
                <select
                  className="input-control input-control-select"
                  name="shippingCountry"
                  value={formData.shippingCountry}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const resolved =
                      countries && countries.length > 0
                        ? (() => {
                            const match = countries.find(
                              (c) =>
                                String(c?.id ?? c?.value ?? '') === String(raw) ||
                                String(c?.label ?? c?.name ?? '').toLowerCase() === String(raw).toLowerCase()
                            );
                            return match ? String(match.id ?? match.value ?? raw) : raw;
                          })()
                        : raw;

                    setFormData((prev) => ({
                      ...prev,
                      shippingCountry: resolved,
                      shippingState: "",
                      shippingCity: "",
                    }));
                    if (resolved) updateListsForCountry(resolved);
                  }}
                >
                  <option value="">Select Country</option>
                  {countries && countries.length > 0 ? (
                    countries.map((country, index) => (
                      <option key={`country-${country.id || country.value || index}`} value={country.id || country.value}>
                        {country.label || country.name}
                      </option>
                    ))
                  ) : (
                    countryOptions.map((country, index) => (
                    <option key={index} value={country}>{country}</option>
                    ))
                  )}
                </select>
              </div>
              <div className="sq-field">
                <label>State</label>
                <select
                  className="input-control input-control-select"
                  name="shippingState"
                  value={formData.shippingState}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const resolved =
                      states && states.length > 0
                        ? (() => {
                            const match = states.find(
                              (s) =>
                                String(s?.id ?? s?.value ?? '') === String(raw) ||
                                String(s?.label ?? s?.name ?? '').toLowerCase() === String(raw).toLowerCase()
                            );
                            return match ? String(match.id ?? match.value ?? raw) : raw;
                          })()
                        : raw;

                    setFormData((prev) => ({
                      ...prev,
                      shippingState: resolved,
                      shippingCity: "",
                    }));
                    // Only fetch cities when we have a numeric state id (API expects :stateId as number)
                    if (resolved && /^\d+$/.test(String(resolved))) {
                      updateListsForState(formData.shippingCountry || "", resolved);
                    } else {
                      updateListsForState("", "");
                    }
                  }}
                >
                  <option value="">Select State</option>
                  {states && states.length > 0 ? (
                    states.map((state, index) => (
                      <option key={`state-${state.id || state.value || index}`} value={state.id || state.value}>
                        {state.label || state.name}
                      </option>
                    ))
                  ) : (
                    stateOptions.map((state, index) => (
                    <option key={index} value={state}>{state}</option>
                    ))
                  )}
                </select>
              </div>
              <div className="sq-field">
                <label>City</label>
                {cities && cities.length > 0 ? (
                  <select
                    className="input-control input-control-select"
                    name="shippingCity"
                    value={formData.shippingCity}
                    onChange={handleInputChange}
                  >
                    <option value="">Select City</option>
                    {cities.map((city, index) => (
                      <option key={`city-${city.id || city.value || index}`} value={city.id || city.value}>
                        {city.label || city.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="input-control"
                    name="shippingCity"
                    value={formData.shippingCity}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                  />
                )}
              </div>
              <div className="sq-field">
                <label>Pincode</label>
                <input
                  type="text"
                  className="input-control"
                  name="shippingPincode"
                  value={formData.shippingPincode}
                  onChange={handleInputChange}
                  placeholder="Pincode"
                />
              </div>
            </div>
          </div>

          {/* Packing Information */}
          <div className="sq-terms-box">
            <h4 className="sq-terms-title">Packing Information</h4>
            <div className="sq-grid-packing-5col">
              <div className="sq-field">
                <label>No. of Packages</label>
                <input
                  type="text"
                  className="input-control"
                  name="noOfPackages"
                  value={formData.noOfPackages}
                  onChange={handleInputChange}
                  placeholder="e.g., 15"
                />
              </div>
              <div className="sq-field">
                <label>Packaging Type</label>
                <select
                  className="input-control input-control-select"
                  name="packagingType"
                  value={formData.packagingType}
                  onChange={handleInputChange}
                >
                  <option value="">Select</option>
                  {packagingTypeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="sq-field">
                <label>Gross Weight (kg)</label>
                <input
                  type="text"
                  className="input-control"
                  name="grossWeight"
                  value={formData.grossWeight}
                  onChange={handleInputChange}
                  placeholder="e.g., 850"
                />
              </div>
              <div className="sq-field">
                <label>Net Weight (kg)</label>
                <input
                  type="text"
                  className="input-control"
                  name="netWeight"
                  value={formData.netWeight}
                  onChange={handleInputChange}
                  placeholder="e.g., 800"
                />
              </div>
              <div className="sq-field">
                <label>Dimensions (L×W×H cm)</label>
                <input
                  type="text"
                  className="input-control"
                  name="dimensions"
                  value={formData.dimensions}
                  onChange={handleInputChange}
                  placeholder="e.g., 100×80×60"
                />
              </div>
            </div>
          </div>

          {/* Additional Notes & Special Instructions */}
          <div className="gi-section gi-notes-section">
            <h4 className="gi-section-subtitle-small">Additional Notes & Special Instructions</h4>
            <textarea
              className="gi-textarea"
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              placeholder="Add any special handling instructions, remarks, or additional information..."
              rows="3"
            />
          </div>

          {/* Declaration */}
          <div className="gi-section gi-declaration-section">
            <h4 className="gi-declaration-title">Declaration</h4>
            <p className="gi-declaration-text">
              We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct. 
              The goods are of India origin and comply with all applicable regulations. 
              <span> This supply is being made under Letter of Undertaking (LUT) without payment of Integrated Tax.</span>
            </p>
          </div>

          {/* Signatures */}
          <div className="gi-signatures-section">
            <div className="gi-signature-left">
              <p className="gi-signature-label">Buyer's Signature & Stamp</p>
            </div>
            <div className="gi-signature-right">
              <p className="gi-signature-company">For ABC Manufacturing Ltd.</p>
              <p className="gi-signature-label">Authorized Signatory</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

